"use client";

import { useReducedMotion } from "motion/react";
import Image from "next/image";
import {
	type CSSProperties,
	memo,
	Profiler,
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../../lib";
import { SpeechBubble } from "../../custom/speech-bubble";
import { StopPreviewSpeechBubbleFace } from "../../custom/stop-preview-speech-bubble-face";
import {
	CONTENT_TOOLTIP_ABILITY_DARK,
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../../design-system/tooltip";
import { coordKey } from "../../hex-path/hex-grid";
import { createSeededRng } from "../../hex-path/seeded-rng";
import { HexagonPulseRing } from "../../hexagon-pulse-ring";
import {
	isContentPointCenteredInViewport,
	resolveScopedPanelMapBandViewportCenterOffsetY,
	usePanZoomSurface,
} from "../../pan-zoom";
import {
	SCOPED_PANEL_MAP_DIM_Z_INDEX,
	SCOPED_PANEL_MAP_PREVIEWER_CHROME_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_ACTIVE_PATH_TILE_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_SPEECH_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_STOP_ICON_Z_INDEX,
	SCOPED_PANEL_MAP_SPOTLIGHT_Z_INDEX,
	SCOPED_PANEL_MAP_VIGNETTE_Z_INDEX,
	SCOPED_PANEL_SPOTLIGHT_DATA_ATTR,
	scopedPanelMapOverlayFadeClassName,
	scopedPanelOverlayClassName,
	scopedPanelSpotlightClassName,
} from "../../scoped-panel/constants";
import type { ScopedPanelSheetInsets } from "../../scoped-panel/scoped-panel-host";
import { SurfaceEdgeVignette } from "../../scoped-panel/surface-edge-vignette";
import { useScopedPanelOverlayVisibility } from "../../scoped-panel/use-scoped-panel-overlay-visibility";
import { Particles } from "../../shadcn/particles";
import {
	GRID_ANIMATION_DURATION_MS,
	GRID_ANIMATION_TOTAL_DURATION_MS,
	type GridAnimationBatchItem,
	type GridAnimationMode,
	resolveGridAnimationCycleDurationMs,
	resolveScaleFadeBatchPlan,
	resolveWipeStepPlan,
} from "../animations";
import {
	resolveKeyedAnimationDelta,
	resolveKeyedAnimationSequence,
} from "../delta-state";
import {
	type EnvironmentImageConfig,
	type PlacedEnvironmentImage,
	placeEnvironmentImages,
} from "../environment-placement";
import { useHexagonMapAnimations } from "../hooks/use-hexagon-map-animations";
import { useHexagonMapCharacterMovement } from "../hooks/use-hexagon-map-character-movement";
import { useHexagonMapPathKeyboard } from "../hooks/use-hexagon-map-path-keyboard";
import { useHexagonMapThumbMovement } from "../hooks/use-hexagon-map-thumb-movement";
import { useScopedPanelSpotlightPathKeys } from "../hooks/use-scoped-panel-spotlight-path-keys";
import {
	buildPathFocusOrder,
	type ComputeMapLayoutOptions,
	resolveMapPathPadding,
	resolvePlayerPathCell,
	resolvePlayerPathIndex,
	shiftLayoutColumns,
	shiftLayoutRows,
} from "../layout-engine";
import {
	computeEnvironmentFootprintPixelBounds,
	resolveEnvironmentSpriteDimensions,
} from "../lib/environment-footprint-bounds";
import {
	applyHexCharacterActiveDomTransition,
	applyHexHoverDomTransition,
	clearHexHoverDomState,
	collectPointerHoverAffectedCellKeys,
	HEX_PATH_TILE_TRAIL_SCALE_OUT_MS,
	type HexCoord,
	type HexGridHoverDomOptions,
	syncHexCellHoverFromPointer,
} from "../lib/hex-grid-hover-dom";
import {
	computeHexagonMapContentBounds,
	type HexagonLayoutMetrics,
	type HexagonMapFitAxis,
	hexCellCenter,
	hexCellPosition,
	resolveHexagonCharacterOverlaySizePx,
	resolveHexagonInnerScale,
	resolveHexagonMapDisplayMetrics,
	resolveHexagonStopOverlaySizePx,
} from "../lib/hexagon-layout-metrics";
import { buildHexagonPathTileClipPaths } from "../lib/hexagon-rounded-clip";
import {
	HEXAGON_MAP_MAX_USER_ZOOM,
	HEXAGON_MAP_MIN_USER_ZOOM,
	HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S,
	HEXAGON_MAP_ZOOM_BUTTON_STEP,
	resolveMapOverlayGapAboveAnchorPx,
	resolveMapSpeechBubbleFloatDelaySec,
} from "../lib/map-overlay-gap";
import {
	buildBranchCorridorIdByCoordKeyForLayout,
	buildCompletedPathCoordKeys,
	buildLockedPathCoordKeys,
	buildPathProgressByCoordKey,
	DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE,
	ensureOnPathCoord,
	findPathTileRoute,
	isOffPathCoord,
	isPathRouteTraversalEnabled,
	isPathTileTraversalEnabled,
	type MapPlayerPathPosition,
	normalizeMapPlayerPathPosition,
	resolveFurthestUnlockedPathProgress,
	resolveFurthestUnlockedStopOnPath,
	resolveMapPlayerPathPositionForPathCell,
	resolveNearestPathTileFromDisplayCenter,
	resolveNextPathCoord,
	resolvePlayerAnchorCoord,
	resolveRestoredPlayerPathCoordAfterLayoutChange,
	resolveScopedPanelNavAvailability,
	type ScopedPanelNavAvailability,
} from "../lib/path-character-movement";
import {
	HEXAGON_MAP_PREVIEW_SPEECH_BUBBLE_SIZE_PX,
	type MapStopPreviewSpeechBubbleModel,
	resolveMapStopPreviewSpotlightContentPoint,
} from "../map-stop-preview-speech-bubble";
import {
	type ResolvedHexagonMapLayout,
	resolveHexagonMapLayout,
} from "../resolve-hexagon-map-layout";
import { HexagonGridTile } from "./HexagonGridTile";
import {
	HexagonMapPlayerCharacterHost,
	type HexagonMapPlayerCharacterRenderArgs,
} from "./HexagonMapPlayerCharacterHost";
import { HexagonMapPlayerFinder } from "./HexagonMapPlayerFinder";
import { HexagonPathConnectorLayer } from "./HexagonPathConnectorLayer";
import { HexagonPathTile } from "./HexagonPathTile";
import "../styles/hexagon-tile-surfaces.css";
import { resolveCharacterRenderCenter } from "../hooks/use-hexagon-map-character-movement";
import {
	HEXAGON_PATH_TILE_DEFAULT_BORDER_WIDTH_PX,
	HEXAGON_PATH_TILE_DEFAULT_OUTER_CORNER_RADIUS_PX,
	resolveHexagonPathFaceScale,
} from "../lib/hexagon-path-tile-appearance";
import {
	getTooltipPlacement,
	radixTooltipFromPlacement,
} from "../lib/tooltip-placement";
import type { MapLayout, ModuleMapGraph, PathCell } from "../map-graph-types";
import { HexagonMapBackdrop } from "./HexagonMapBackdrop";
import type { HexagonMapCornerOctagonButtonConfig } from "./HexagonMapControls";
import { HexagonMapControls } from "./HexagonMapControls";
import { HexagonMapDemoPathStopTooltip } from "./tooltips/HexagonMapDemoPathStopTooltip";

export type { HexagonMapPlayerCharacterRenderArgs };

export interface PlayerIcons {
	upRight: string;
	right: string;
	downRight: string;
	upLeft: string;
	left: string;
	downLeft: string;
}

export interface PathStopRenderContext {
	stopId: string;
	row: number;
	col: number;
	isUnlocked: boolean;
	isCompleted: boolean;
	isHighlighted: boolean;
	iconSrc: string;
}

export interface HexagonMapProps {
	graph: ModuleMapGraph;
	layoutSeed: string;
	layoutSeedMode?: "exact" | "search";
	layoutOptions?: Omit<ComputeMapLayoutOptions, "seed">;
	backdropJpg: string;
	environmentImages?: EnvironmentImageConfig[];
	pathStopIcons: Record<string, string>;
	/** stopId → portrait image for battle/scenario speech bubbles above path tiles. */
	pathStopSpeechBubbles?: Record<string, string>;
	/** stopId → short label shown in a pill under the stop tile (e.g. marketplace "Free preview", D41). */
	pathStopBadges?: Readonly<Record<string, string>>;
	/**
	 * When false the character never walks to a pressed / previewed stop (camera only; marketplace-ea D23:
	 * the avatar travels only where the learner can actually go). Defaults to true.
	 */
	characterWalksToStops?: boolean;
	unlockedStopIds: Set<string>;
	completedStopIds?: Set<string>;
	playerStopId?: string | null;
	playerPathPosition?: MapPlayerPathPosition | null;
	onPlayerPathPositionChange?: (position: MapPlayerPathPosition) => void;
	playerIcons: PlayerIcons;
	/**
	 * Caller-owned player body (Learn 3D / marketing PNG).
	 * Host owns pin/spotlight chrome; this renders the character body only.
	 */
	renderPlayerCharacter: (
		args: HexagonMapPlayerCharacterRenderArgs,
	) => ReactNode;
	highlightedStopId?: string | null;
	demoMode?: boolean;
	isTranslucent?: boolean;
	showAuthoringControls?: boolean;
	onResolvedLayoutSeedChange?: (seed: string) => void;
	/** Fired when layout generation fails (e.g. invalid settings). */
	onLayoutGenerationFailed?: () => void;
	onPathStopPress?: (stopId: string) => void;
	/** Fired when the player selects a path cell (stop id when the cell hosts a stop, else null). */
	onMapPathSelectionChange?: (stopId: string | null) => void;
	/** When set, pans the path character toward this stop (same as clicking the stop on the map). */
	requestedNavigateStopId?: string | null;
	onRequestedNavigateStopHandled?: () => void;
	onKeyboardDismiss?: () => void;
	renderPathStopTooltip?: (ctx: PathStopRenderContext) => ReactNode;
	gridAnimationMode?: GridAnimationMode;
	gridAnimationDurationMs?: number;
	gridAnimationTotalDurationMs?: number;
	environmentAnimationTotalDurationMs?: number;
	initialAnimationEnabled?: boolean;
	onInitialStagePreloadReady?: () => void;
	/**
	 * Bump this value to force the initial stage preload callback to re-fire for the
	 * same layout/size (useful when the page is restored from cache and we want the
	 * enter animations to replay).
	 */
	initialStagePreloadNonce?: string | number;
	/** Outer rounded corner radius for path tiles (px). */
	pathTileOuterCornerRadiusPx?: number;
	/** Frosted border band width between outer and inner path hex (px). */
	pathTileBorderWidthPx?: number;
	characterMovementMsPerTile?: number;
	/** Player portrait for the off-screen finder bubble (header avatar). */
	playerAvatarSrc?: string | null;
	/** Map pin floated above the path character (zoom-agnostic sizing). */
	playerPinSrc?: string | null;
	layoutErrorActions?: ReactNode;
	/** When set, dims the map and elevates this stop's tile, character, and speech bubble. */
	scopedPanelSpotlightStopId?: string | null;
	/** Replaces the map speech bubble for the spotlight stop while preview sheets are open. */
	scopedPanelPreviewSpeechBubble?: MapStopPreviewSpeechBubbleModel | null;
	onScopedPanelDismiss?: () => void;
	/** Prev/next stop availability for scoped preview overlay chevrons. */
	onScopedPanelNavAvailabilityChange?: (
		availability: ScopedPanelNavAvailability | null,
	) => void;
	/** Imperative pan/zoom to a stop tile (fires on every token, even when preview stop id is unchanged). */
	requestedViewportFocus?: MapViewportFocusRequest | null;
	/** Bumped by the parent on each scoped preview stop selection to force character walks. */
	scopedPanelNavigationToken?: number;
	/** Top/bottom sheet heights for centering the spotlight in the visible map band. */
	scopedPanelSheetInsets?: ScopedPanelSheetInsets | null;
	/** Map settings / review-notes controls; fixed on the pan-zoom viewport. */
	previewerChrome?: ReactNode;
	onMapViewportElement?: (element: HTMLDivElement | null) => void;
	topLeftOctagonButton?: HexagonMapCornerOctagonButtonConfig;
	topRightOctagonButton?: HexagonMapCornerOctagonButtonConfig;
	/** Bottom-left thumb joystick for path character movement (learner map). */
	showThumbMovementControls?: boolean;
	className?: string;
}

export interface MapViewportFocusRequest {
	stopId: string;
	token: number;
}

const PATH_TILE_CLICK_MAX_MOVE_PX = 8;
const EMPTY_SPOTLIGHT_COORD_KEYS = new Set<string>();

interface HexagonGridCellModel {
	key: string;
	row: number;
	col: number;
	left: number;
	top: number;
	isPath: boolean;
	pathIndex: number;
	isCompleted: boolean;
	stopId: string | null;
}

type GridAnimationPhase = "stable" | "enter" | "exit";

interface AnimatedHexagonGridCellModel extends HexagonGridCellModel {
	animationDelayMs: number;
	animationPhase: GridAnimationPhase;
}

interface EnvironmentPlacementModel extends PlacedEnvironmentImage {
	key: string;
	left: number;
	top: number;
	width: number;
	height: number;
	animationDelayMs: number;
	animationPhase: GridAnimationPhase;
}

interface HexagonStopCellModel {
	nodeId: string;
	row: number;
	col: number;
	pathIndex: number;
	left: number;
	top: number;
	stopId: string | null;
	stopType: "TOPIC" | "BATTLE" | "SCENARIO" | "BOSS" | "ACTION" | null;
	iconKey: string;
	iconSrc: string;
	speechBubbleSrc: string | null;
	isUnlocked: boolean;
	isCompleted: boolean;
	focusOrderIndex: number;
	tooltipPlacement: ReturnType<typeof radixTooltipFromPlacement>;
}

interface HexagonPointerPosition {
	clientX: number;
	clientY: number;
}

const HEXAGON_MAP_HOVER_SCALE = 1.5;
const HEXAGON_MAP_GRID_OVERSCAN = 1;
/** Above character (z-40) and stops (z-30); below Radix tooltips (z-[200] portal). */
const HEXAGON_MAP_SPEECH_BUBBLE_Z_INDEX = 45;
/** Map portrait bubble face size — ~20% smaller than default SpeechBubble. */
const HEXAGON_MAP_SPEECH_BUBBLE_SIZE_PX = 58;

function getLayoutErrorMessage(error: unknown): string {
	return error instanceof Error
		? error.message
		: "Failed to resolve hexagon map layout.";
}

function logHexagonMapPerfEvent(
	event: string,
	payload: Record<string, unknown>,
): void {
	console.info(`[hexagon-map] ${event}`, payload);
}

function HexagonMapLayerProfiler({
	id,
	children,
	minDurationMs = 8,
}: {
	id: string;
	children: ReactNode;
	minDurationMs?: number;
}) {
	return (
		<Profiler
			id={id}
			onRender={(
				profilerId,
				phase,
				actualDuration,
				baseDuration,
				startTime,
				commitTime,
			) => {
				if (phase !== "mount" && actualDuration < minDurationMs) {
					return;
				}
				logHexagonMapPerfEvent("layer render", {
					id: profilerId,
					phase,
					actualDurationMs: Math.round(actualDuration),
					baseDurationMs: Math.round(baseDuration),
					startTimeMs: Math.round(startTime),
					commitTimeMs: Math.round(commitTime),
				});
			}}
		>
			{children}
		</Profiler>
	);
}

const HEXAGON_MAP_LAYOUT_CACHE_LIMIT = 24;
const resolvedLayoutCache = new Map<string, ResolvedHexagonMapLayout>();

function serializeLayoutGraph(graph: ModuleMapGraph): string {
	return JSON.stringify({
		nodes: graph.nodes.map((node) => {
			if (node.kind === "content") {
				return {
					kind: node.kind,
					id: node.id,
					stopId: node.stopId,
					chapterId: node.chapterId,
					branchId: node.branchId ?? null,
					branchRejoins: node.branchRejoins ?? false,
					gating: node.gating,
					stopType: node.stopType,
				};
			}
			if (node.kind === "spacer") {
				return {
					kind: node.kind,
					id: node.id,
					branchId: node.branchId ?? null,
					branchRejoins: node.branchRejoins ?? false,
				};
			}
			return {
				kind: node.kind,
				id: node.id,
				chapterId: node.chapterId,
			};
		}),
		branchCorridorIds: graph.branchCorridorIds,
	});
}

function serializeLayoutOptions(
	layoutOptions?: Omit<ComputeMapLayoutOptions, "seed">,
): string {
	if (!layoutOptions) {
		return "";
	}
	return JSON.stringify({
		direction: layoutOptions.direction ?? null,
		pathPadding: layoutOptions.pathPadding ?? null,
		yMinRange: layoutOptions.yMinRange ?? null,
		yMaxRange: layoutOptions.yMaxRange ?? null,
		majorRangePercent: layoutOptions.majorRangePercent ?? null,
		xMaxStepsBack: layoutOptions.xMaxStepsBack ?? null,
		xMaxStepsForward: layoutOptions.xMaxStepsForward ?? null,
		minorRange: layoutOptions.minorRange ?? null,
		maxConsecutiveSameDirectionSteps:
			layoutOptions.maxConsecutiveSameDirectionSteps ?? null,
		maxAttempts: layoutOptions.maxAttempts ?? null,
		maxDurationMs: layoutOptions.maxDurationMs ?? null,
		logAttempts: layoutOptions.logAttempts ?? null,
		forceStraightSpine: layoutOptions.forceStraightSpine ?? null,
	});
}

function getCachedResolvedLayout(
	cacheKey: string,
): ResolvedHexagonMapLayout | null {
	const cached = resolvedLayoutCache.get(cacheKey);
	if (!cached) {
		return null;
	}
	resolvedLayoutCache.delete(cacheKey);
	resolvedLayoutCache.set(cacheKey, cached);
	return cached;
}

function setCachedResolvedLayout(
	cacheKey: string,
	value: ResolvedHexagonMapLayout,
): ResolvedHexagonMapLayout {
	resolvedLayoutCache.set(cacheKey, value);
	while (resolvedLayoutCache.size > HEXAGON_MAP_LAYOUT_CACHE_LIMIT) {
		const oldestKey = resolvedLayoutCache.keys().next().value;
		if (!oldestKey) {
			break;
		}
		resolvedLayoutCache.delete(oldestKey);
	}
	return value;
}

function isSameHexCoord(
	a: { row: number; col: number } | null,
	b: { row: number; col: number } | null,
): boolean {
	return a?.row === b?.row && a?.col === b?.col;
}

function resolveHoveredHexFromPointer(params: {
	pointer: HexagonPointerPosition;
	stageRect: DOMRect;
	scale: number;
	metrics: HexagonLayoutMetrics;
	rows: number;
	cols: number;
}): { row: number; col: number } | null {
	const { pointer, stageRect, scale, metrics, rows, cols } = params;
	if (rows <= 0 || cols <= 0 || scale <= 0) {
		return null;
	}
	const localX = (pointer.clientX - stageRect.left) / scale;
	const localY = (pointer.clientY - stageRect.top) / scale;
	const rowCenterStart =
		Math.max(0, -metrics.computedMarginTop) +
		metrics.computedMarginTop +
		metrics.hexagonHeight / 2;
	const row = Math.round(
		(localY - rowCenterStart) / (metrics.rowSpacing * 1.075),
	);
	if (row < 0 || row >= rows) {
		return null;
	}
	const isOddRow = (row + 1) % 2 === 1;
	const rowLeftOffset = isOddRow
		? metrics.oddRowMarginLeft
		: metrics.evenRowMarginLeft;
	const cellCenterStart =
		metrics.contentInsetLeft +
		rowLeftOffset -
		10 +
		metrics.hexagonWidth / 2;
	const col = Math.round(
		(localX - cellCenterStart) /
			(metrics.hexagonWidth + metrics.hexagonMargin),
	);
	if (col < 0 || col >= cols) {
		return null;
	}
	return { row, col };
}

function resolveAnimatedGridCellDelayMs(params: {
	cell: Pick<HexagonGridCellModel, "row" | "col">;
	mode: GridAnimationMode;
	rows: number;
	animationDurationMs: number;
	totalDurationMs: number;
	phase: "enter" | "exit";
	scaleFadeStartTimeByKey?: Map<string, number>;
	cellKey: string;
}): number {
	const {
		cell,
		mode,
		rows,
		animationDurationMs,
		totalDurationMs,
		phase,
		scaleFadeStartTimeByKey,
		cellKey,
	} = params;
	if (mode === "scale-fade") {
		return scaleFadeStartTimeByKey?.get(cellKey) ?? 0;
	}
	const wipeStepPlan = resolveWipeStepPlan({
		stepCount: Math.max(1, rows),
		config: {
			gridAnimationDurationMs: animationDurationMs,
			gridAnimationTotalDurationMs: totalDurationMs,
		},
	});
	const wipeIndex =
		phase === "exit" ? cell.row : Math.max(0, rows - 1 - cell.row);
	return wipeStepPlan.startTimesMs[wipeIndex] ?? 0;
}

function buildAnimatedGridCells(params: {
	cells: HexagonGridCellModel[];
	layoutSeed: string;
	mode: GridAnimationMode;
	rows: number;
	animationDurationMs: number;
	totalDurationMs: number;
	phase: GridAnimationPhase;
	layerKey?: string;
}): AnimatedHexagonGridCellModel[] {
	const {
		cells,
		layoutSeed,
		mode,
		rows,
		animationDurationMs,
		totalDurationMs,
		phase,
		layerKey,
	} = params;
	if (phase === "stable") {
		return cells.map((cell) => ({
			...cell,
			animationDelayMs: 0,
			animationPhase: phase,
		}));
	}
	const scaleFadeStartTimeByKey =
		mode === "scale-fade"
			? new Map(
					resolveScaleFadeBatchPlan({
						items: cells as GridAnimationBatchItem[],
						layoutSeed,
						layerKey,
						config: {
							gridAnimationDurationMs: animationDurationMs,
							gridAnimationTotalDurationMs: totalDurationMs,
						},
					}).assignments.map((assignment) => [
						assignment.key,
						assignment.startTimeMs,
					]),
				)
			: undefined;
	return cells.map((cell) => ({
		...cell,
		animationDelayMs: resolveAnimatedGridCellDelayMs({
			cell,
			mode,
			rows,
			animationDurationMs,
			totalDurationMs,
			phase,
			scaleFadeStartTimeByKey,
			cellKey: cell.key,
		}),
		animationPhase: phase,
	}));
}

function buildAnimatedEnvironmentPlacements(params: {
	placements: EnvironmentPlacementModel[];
	layoutSeed: string;
	mode: GridAnimationMode;
	rows: number;
	animationDurationMs: number;
	totalDurationMs: number;
	phase: GridAnimationPhase;
	layerKey?: string;
	baseDelayMs?: number;
}): EnvironmentPlacementModel[] {
	const {
		placements,
		layoutSeed,
		mode,
		rows,
		animationDurationMs,
		totalDurationMs,
		phase,
		layerKey = "environment",
		baseDelayMs = 0,
	} = params;
	if (phase === "stable") {
		return placements.map((placement) => ({
			...placement,
			animationDelayMs: 0,
			animationPhase: phase,
		}));
	}
	const scaleFadeStartTimeByKey =
		mode === "scale-fade"
			? new Map(
					resolveScaleFadeBatchPlan({
						items: placements as GridAnimationBatchItem[],
						layoutSeed,
						layerKey,
						config: {
							gridAnimationDurationMs: animationDurationMs,
							gridAnimationTotalDurationMs: totalDurationMs,
						},
					}).assignments.map((assignment) => [
						assignment.key,
						assignment.startTimeMs,
					]),
				)
			: undefined;
	return placements.map((placement) => ({
		...placement,
		animationDelayMs:
			baseDelayMs +
			resolveAnimatedGridCellDelayMs({
				cell: placement,
				mode,
				rows,
				animationDurationMs,
				totalDurationMs,
				phase,
				scaleFadeStartTimeByKey,
				cellKey: placement.key,
			}),
		animationPhase: phase,
	}));
}

function resolveGridAnimationStyle(params: {
	animationPhase: GridAnimationPhase;
	animationMode: GridAnimationMode;
	animationDurationMs: number;
	animationDelayMs: number;
}): CSSProperties {
	const {
		animationPhase,
		animationMode,
		animationDurationMs,
		animationDelayMs,
	} = params;
	if (animationPhase === "stable") {
		return {};
	}
	if (animationMode === "wipe") {
		return {
			animation:
				animationPhase === "enter"
					? `sq-hex-map-wipe-in ${animationDurationMs}ms cubic-bezier(.4,0,.2,1) both`
					: `sq-hex-map-wipe-out ${animationDurationMs}ms cubic-bezier(.4,0,.2,1) both`,
			animationDelay: `${animationDelayMs}ms`,
		};
	}
	return {
		animation:
			animationPhase === "enter"
				? `sq-hex-map-scale-fade-in ${animationDurationMs}ms cubic-bezier(.4,2,.35,1) both`
				: `sq-hex-map-scale-fade-out ${animationDurationMs}ms cubic-bezier(.4,2,.35,1) both`,
		animationDelay: `${animationDelayMs}ms`,
	};
}

interface HexagonGridCellProps {
	model: AnimatedHexagonGridCellModel;
	renderAsPath: boolean;
	isTranslucent: boolean;
	isCharacterPathTile: boolean;
	isVisible: boolean;
	animationMode: GridAnimationMode;
	animationDurationMs: number;
	positionTransitionMs: number;
	registerHoverCellRoot: (key: string, node: HTMLElement | null) => void;
	isScopedPanelSpotlight?: boolean;
	isScopedPanelSpotlightExiting?: boolean;
	scopedPanelSpotlightExitFromCharacter?: boolean;
	isPathTileLocked?: boolean;
}

function hexagonGridCellPropsAreEqual(
	prev: HexagonGridCellProps,
	next: HexagonGridCellProps,
): boolean {
	return (
		prev.model.key === next.model.key &&
		prev.model.left === next.model.left &&
		prev.model.top === next.model.top &&
		prev.model.isCompleted === next.model.isCompleted &&
		prev.model.animationPhase === next.model.animationPhase &&
		prev.model.animationDelayMs === next.model.animationDelayMs &&
		prev.renderAsPath === next.renderAsPath &&
		prev.isTranslucent === next.isTranslucent &&
		prev.isCharacterPathTile === next.isCharacterPathTile &&
		prev.isVisible === next.isVisible &&
		prev.isScopedPanelSpotlight === next.isScopedPanelSpotlight &&
		prev.isScopedPanelSpotlightExiting ===
			next.isScopedPanelSpotlightExiting &&
		prev.scopedPanelSpotlightExitFromCharacter ===
			next.scopedPanelSpotlightExitFromCharacter &&
		prev.isPathTileLocked === next.isPathTileLocked &&
		prev.animationMode === next.animationMode &&
		prev.animationDurationMs === next.animationDurationMs &&
		prev.positionTransitionMs === next.positionTransitionMs &&
		prev.registerHoverCellRoot === next.registerHoverCellRoot
	);
}

/** Spotlight route tiles: character cell full opacity, others dimmed (matches path trail timing). */
const SCOPED_PANEL_SPOTLIGHT_PATH_TILE_DIMMED_OPACITY = 0.5;
const LOCKED_PATH_TILE_OPACITY = 0.5;
/** Stop icon on the tile under the player — bubble carries full-opacity icon. */
const CHARACTER_ON_STOP_ICON_OPACITY = 0.3;

const HexagonGridCell = memo(function HexagonGridCell({
	model,
	renderAsPath,
	isTranslucent,
	isCharacterPathTile,
	isVisible,
	animationMode,
	animationDurationMs,
	positionTransitionMs,
	registerHoverCellRoot,
	isScopedPanelSpotlight = false,
	isScopedPanelSpotlightExiting = false,
	scopedPanelSpotlightExitFromCharacter = false,
	isPathTileLocked = false,
}: HexagonGridCellProps) {
	const isSpotlightPathTile =
		isScopedPanelSpotlight &&
		renderAsPath &&
		!isScopedPanelSpotlightExiting;
	const isSpotlightPathTileExiting =
		isScopedPanelSpotlight && renderAsPath && isScopedPanelSpotlightExiting;
	const spotlightPathOpacity = !isVisible
		? 0
		: isSpotlightPathTileExiting || isSpotlightPathTile
			? isCharacterPathTile && !isScopedPanelSpotlightExiting
				? 1
				: SCOPED_PANEL_SPOTLIGHT_PATH_TILE_DIMMED_OPACITY
			: 1;
	const spotlightExitScaleRef = useRef<HTMLDivElement | null>(null);
	useLayoutEffect(() => {
		const root = spotlightExitScaleRef.current;
		if (!root || !isSpotlightPathTileExiting) {
			return;
		}
		const startScale = scopedPanelSpotlightExitFromCharacter
			? HEXAGON_MAP_HOVER_SCALE
			: 1;
		root.style.setProperty("--hex-cell-hover-scale", String(startScale));
		const frame = requestAnimationFrame(() => {
			root.style.setProperty("--hex-cell-hover-scale", "1");
		});
		return () => {
			cancelAnimationFrame(frame);
		};
	}, [
		isSpotlightPathTileExiting,
		model.key,
		scopedPanelSpotlightExitFromCharacter,
	]);
	const lockedPathOpacity =
		renderAsPath && isPathTileLocked && !isSpotlightPathTile
			? isVisible
				? LOCKED_PATH_TILE_OPACITY
				: 0
			: undefined;

	return (
		<div
			ref={(node) => {
				spotlightExitScaleRef.current = node;
				registerHoverCellRoot(model.key, node);
			}}
			data-hex-cell-key={model.key}
			data-hex-path-cell={renderAsPath ? "true" : undefined}
			data-spotlight-exiting={
				isSpotlightPathTileExiting ? "true" : undefined
			}
			{...(isScopedPanelSpotlight
				? { [SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]: "path-tile" }
				: {})}
			className={cn(
				"pointer-events-none absolute overflow-visible",
				renderAsPath && "z-10",
				isScopedPanelSpotlight && scopedPanelSpotlightClassName,
			)}
			style={{
				left: model.left,
				top: model.top,
				width: "var(--hexagon-width)",
				height: "var(--hexagon-height)",
				transform: "scale(var(--hex-cell-hover-scale, 1))",
				transition: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), width ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), height ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), transform var(--sq-hex-cell-scale-duration) var(--sq-hex-cell-scale-easing)`,
				transformOrigin: "50% 50%",
				...(isScopedPanelSpotlight
					? {
							zIndex:
								isCharacterPathTile &&
								!isScopedPanelSpotlightExiting
									? SCOPED_PANEL_MAP_SPOTLIGHT_ACTIVE_PATH_TILE_Z_INDEX
									: SCOPED_PANEL_MAP_SPOTLIGHT_Z_INDEX,
						}
					: {}),
			}}
		>
			{/*
			 * Stop-preview pulse: same inner clip as path tiles (not outer —
			 * Safari falls back to circles on `--hex-tile-outer-clip`).
			 */}
			{isSpotlightPathTile && isCharacterPathTile ? (
				<HexagonPulseRing className="z-0" />
			) : null}
			<div
				className={cn(
					"relative z-[1] size-full transition-opacity ease",
					isSpotlightPathTile || isSpotlightPathTileExiting
						? undefined
						: "duration-300 [transition-timing-function:cubic-bezier(.4,2,.35,1)]",
					!isSpotlightPathTile &&
						!isSpotlightPathTileExiting &&
						!isPathTileLocked &&
						(lockedPathOpacity == null
							? isVisible
								? "opacity-100"
								: "opacity-0"
							: undefined),
				)}
				style={{
					...resolveGridAnimationStyle({
						animationPhase: isVisible
							? model.animationPhase
							: "stable",
						animationMode,
						animationDurationMs,
						animationDelayMs: model.animationDelayMs,
					}),
					transformOrigin: "50% 50%",
					...(isSpotlightPathTile || isSpotlightPathTileExiting
						? {
								opacity: spotlightPathOpacity,
								transitionDuration: `${HEX_PATH_TILE_TRAIL_SCALE_OUT_MS}ms`,
							}
						: lockedPathOpacity != null
							? { opacity: lockedPathOpacity }
							: {}),
				}}
			>
				{renderAsPath ? (
					<div className="relative size-full">
						<HexagonGridTile isTranslucent={isTranslucent} />
						<HexagonPathTile
							isTranslucent={isTranslucent}
							isCompleted={model.isCompleted}
							isCharacterActive={isCharacterPathTile}
						/>
					</div>
				) : (
					<HexagonGridTile isTranslucent={isTranslucent} />
				)}
			</div>
		</div>
	);
}, hexagonGridCellPropsAreEqual);

const HexagonGridLayer = memo(function HexagonGridLayer({
	cells,
	isTranslucent,
	animationMode,
	animationDurationMs,
	positionTransitionMs,
	registerHoverCellRoot,
}: {
	cells: AnimatedHexagonGridCellModel[];
	isTranslucent: boolean;
	animationMode: GridAnimationMode;
	animationDurationMs: number;
	positionTransitionMs: number;
	registerHoverCellRoot: (key: string, node: HTMLElement | null) => void;
}) {
	return cells.map((cell) => (
		<HexagonGridCell
			key={cell.key}
			model={cell}
			renderAsPath={false}
			isTranslucent={isTranslucent}
			isCharacterPathTile={false}
			isVisible
			animationMode={animationMode}
			animationDurationMs={animationDurationMs}
			positionTransitionMs={positionTransitionMs}
			registerHoverCellRoot={registerHoverCellRoot}
		/>
	));
});

type ScopedPanelMapLayerPass = "full" | "below-dim" | "above-dim";

function shouldRenderScopedPanelMapLayerPass(
	pass: ScopedPanelMapLayerPass,
	isSpotlight: boolean,
): boolean {
	if (pass === "full") {
		return true;
	}
	if (pass === "below-dim") {
		return !isSpotlight;
	}
	return isSpotlight;
}

const HexagonPathOverlayLayer = memo(function HexagonPathOverlayLayer({
	cells,
	isTranslucent,
	activePlayerCoord,
	isPathIndexVisible,
	positionTransitionMs,
	registerHoverCellRoot,
	scopedPanelSpotlightRenderCoordKeys,
	scopedPanelSpotlightActiveRouteCoordKeys,
	scopedPanelSpotlightExitingTiles,
	lockedPathCoordKeys,
	layerPass = "full",
}: {
	cells: AnimatedHexagonGridCellModel[];
	isTranslucent: boolean;
	activePlayerCoord: { row: number; col: number } | null;
	isPathIndexVisible: (pathIndex: number) => boolean;
	positionTransitionMs: number;
	registerHoverCellRoot: (key: string, node: HTMLElement | null) => void;
	scopedPanelSpotlightRenderCoordKeys: ReadonlySet<string>;
	scopedPanelSpotlightActiveRouteCoordKeys: ReadonlySet<string>;
	scopedPanelSpotlightExitingTiles: ReadonlyMap<
		string,
		{ key: string; exitFromCharacter: boolean }
	>;
	lockedPathCoordKeys: ReadonlySet<string>;
	layerPass?: ScopedPanelMapLayerPass;
}) {
	return cells.map((cell) => {
		const isCharacterTile =
			activePlayerCoord != null &&
			activePlayerCoord.row === cell.row &&
			activePlayerCoord.col === cell.col;
		const isScopedPanelSpotlight = scopedPanelSpotlightRenderCoordKeys.has(
			cell.key,
		);
		const exitState = scopedPanelSpotlightExitingTiles.get(cell.key);
		const isScopedPanelSpotlightExiting =
			exitState != null &&
			!scopedPanelSpotlightActiveRouteCoordKeys.has(cell.key);
		const pathVisible =
			isScopedPanelSpotlight || isPathIndexVisible(cell.pathIndex);
		if (
			!shouldRenderScopedPanelMapLayerPass(
				layerPass,
				isScopedPanelSpotlight,
			)
		) {
			return null;
		}
		return (
			<HexagonGridCell
				key={`path:${cell.key}`}
				model={cell}
				renderAsPath
				isTranslucent={isTranslucent}
				isCharacterPathTile={isCharacterTile}
				isVisible={pathVisible}
				isScopedPanelSpotlight={isScopedPanelSpotlight}
				isScopedPanelSpotlightExiting={isScopedPanelSpotlightExiting}
				scopedPanelSpotlightExitFromCharacter={
					exitState?.exitFromCharacter ?? false
				}
				isPathTileLocked={lockedPathCoordKeys.has(cell.key)}
				animationMode="wipe"
				animationDurationMs={positionTransitionMs}
				positionTransitionMs={positionTransitionMs}
				registerHoverCellRoot={registerHoverCellRoot}
			/>
		);
	});
});

const HexagonEnvironmentSprite = memo(function HexagonEnvironmentSprite({
	model,
	animationMode,
	animationDurationMs,
	positionTransitionMs,
}: {
	model: EnvironmentPlacementModel;
	animationMode: GridAnimationMode;
	animationDurationMs: number;
	positionTransitionMs: number;
}) {
	return (
		<div
			className="pointer-events-none absolute z-10"
			style={{
				left: model.left,
				top: model.top,
				width: model.width,
				height: model.height,
				transform: `translate(-50%, -60%)${model.flipHorizontal ? " scaleX(-1)" : ""}`,
				transition: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), width ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), height ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1)`,
			}}
		>
			<div
				className="size-full"
				style={resolveGridAnimationStyle({
					animationPhase: model.animationPhase,
					animationMode,
					animationDurationMs,
					animationDelayMs: model.animationDelayMs,
				})}
			>
				<Image
					src={model.src}
					alt=""
					width={model.width}
					height={model.height}
					className="size-full object-contain opacity-95"
				/>
			</div>
		</div>
	);
});

/** Labels in a pill under stop tiles; counter-scaled like the speech bubbles so they stay readable. */
const HexagonStopBadgeLayer = memo(function HexagonStopBadgeLayer({
	stopCells,
	badges,
	contentMapScale,
	hexagonHeight,
	isPathIndexVisible,
	positionTransitionMs,
}: {
	stopCells: HexagonStopCellModel[];
	badges: Readonly<Record<string, string>>;
	contentMapScale: number;
	hexagonHeight: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
	positionTransitionMs: number;
}) {
	const inverseScale = contentMapScale > 0 ? 1 / contentMapScale : 1;
	return stopCells.map((cell) => {
		const label = cell.stopId ? badges[cell.stopId] : undefined;
		if (!label) {
			return null;
		}
		return (
			<div
				key={`badge:${cell.nodeId}`}
				className={cn(
					"pointer-events-none absolute whitespace-nowrap rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-foreground shadow-sm ring-1 ring-border",
					isPathIndexVisible(cell.pathIndex)
						? "opacity-100"
						: "opacity-0",
				)}
				style={{
					zIndex: HEXAGON_MAP_SPEECH_BUBBLE_Z_INDEX,
					left: cell.left,
					// Clear of the player character, which stands on (and below) its tile.
					top: cell.top + hexagonHeight * 0.7,
					transform: `translate(-50%, 0) scale(${inverseScale})`,
					transformOrigin: "center top",
					transition: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top 200ms cubic-bezier(.22,1,.36,1), opacity 0.25s cubic-bezier(.4,2,.35,1)`,
				}}
			>
				{label}
			</div>
		);
	});
});

const HexagonStopSpeechBubbleLayer = memo(
	function HexagonStopSpeechBubbleLayer({
		stopCells,
		contentMapScale,
		userZoom,
		hexagonHeight,
		isPathIndexVisible,
		positionTransitionMs,
		scopedPanelSpotlightStopId = null,
		scopedPanelPreviewSpeechBubble = null,
		layerPass = "full",
	}: {
		stopCells: HexagonStopCellModel[];
		contentMapScale: number;
		userZoom: number;
		hexagonHeight: number;
		isPathIndexVisible: (pathIndex: number) => boolean;
		positionTransitionMs: number;
		scopedPanelSpotlightStopId?: string | null;
		scopedPanelPreviewSpeechBubble?: MapStopPreviewSpeechBubbleModel | null;
		layerPass?: ScopedPanelMapLayerPass;
	}) {
		const inverseScale = contentMapScale > 0 ? 1 / contentMapScale : 1;
		const gapAboveTilePx = resolveMapOverlayGapAboveAnchorPx(userZoom);
		const previewStopId = scopedPanelPreviewSpeechBubble?.stopId ?? null;

		return stopCells.map((cell) => {
			const floatDelaySec = resolveMapSpeechBubbleFloatDelaySec(
				cell.stopId ?? cell.nodeId,
			);
			const isPreviewStop =
				previewStopId != null && cell.stopId === previewStopId;
			const isScopedPanelSpotlight =
				scopedPanelSpotlightStopId != null &&
				cell.stopId === scopedPanelSpotlightStopId;
			const isAboveDimSpeech =
				(isPreviewStop && scopedPanelPreviewSpeechBubble != null) ||
				isScopedPanelSpotlight;
			if (
				!shouldRenderScopedPanelMapLayerPass(
					layerPass,
					isAboveDimSpeech,
				)
			) {
				return null;
			}
			if (isPreviewStop && scopedPanelPreviewSpeechBubble) {
				const isVisible = isPathIndexVisible(cell.pathIndex);
				const tileTop = cell.top - hexagonHeight / 2;
				return (
					<div
						key={`speech-preview:${cell.nodeId}`}
						{...{
							[SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]:
								"speech-bubble-preview",
						}}
						className={cn(
							"pointer-events-none absolute",
							scopedPanelSpotlightClassName,
							isVisible ? "opacity-100" : "opacity-0",
						)}
						style={{
							zIndex: SCOPED_PANEL_MAP_SPOTLIGHT_SPEECH_Z_INDEX,
							left: cell.left,
							top: tileTop - gapAboveTilePx,
							transform: `translate(-50%, -100%) scale(${inverseScale})`,
							transformOrigin: "center bottom",
							transition: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top 200ms cubic-bezier(.22,1,.36,1), opacity 0.25s cubic-bezier(.4,2,.35,1)`,
						}}
					>
						<SpeechBubble
							faceShape="rounded-3xl"
							arrowDirection="down"
							variant="light"
							sizePx={HEXAGON_MAP_PREVIEW_SPEECH_BUBBLE_SIZE_PX}
							floatDurationSec={
								HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S
							}
							floatDelaySec={floatDelaySec}
						>
							<StopPreviewSpeechBubbleFace
								model={scopedPanelPreviewSpeechBubble}
								sizePx={
									HEXAGON_MAP_PREVIEW_SPEECH_BUBBLE_SIZE_PX
								}
							/>
						</SpeechBubble>
					</div>
				);
			}

			if (!cell.speechBubbleSrc || isPreviewStop) {
				return null;
			}
			const isVisible = isPathIndexVisible(cell.pathIndex);
			const tileTop = cell.top - hexagonHeight / 2;
			return (
				<div
					key={`speech:${cell.nodeId}`}
					{...(isScopedPanelSpotlight
						? {
								[SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]:
									"speech-bubble",
							}
						: {})}
					className={cn(
						"pointer-events-none absolute",
						isScopedPanelSpotlight && scopedPanelSpotlightClassName,
						isVisible ? "opacity-100" : "opacity-0",
					)}
					style={{
						zIndex: isScopedPanelSpotlight
							? SCOPED_PANEL_MAP_SPOTLIGHT_SPEECH_Z_INDEX
							: HEXAGON_MAP_SPEECH_BUBBLE_Z_INDEX,
						left: cell.left,
						top: tileTop - gapAboveTilePx,
						transform: `translate(-50%, -100%) scale(${inverseScale})`,
						transformOrigin: "center bottom",
						transition: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top 200ms cubic-bezier(.22,1,.36,1), opacity 0.25s cubic-bezier(.4,2,.35,1)`,
					}}
				>
					<SpeechBubble
						imageSrc={cell.speechBubbleSrc}
						arrowDirection="down"
						variant="light"
						sizePx={HEXAGON_MAP_SPEECH_BUBBLE_SIZE_PX}
						floatDurationSec={HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S}
						floatDelaySec={floatDelaySec}
					/>
				</div>
			);
		});
	},
);

const HexagonStopMarker = memo(function HexagonStopMarker({
	cell,
	hoveredHex,
	focusedPathIndex,
	demoMode,
	pathStopIcons,
	renderPathStopTooltip,
	highlightedStopId,
	stopButtonRefs,
	handlePathStopClick,
	isPathIndexVisible,
	activePlayerCoord,
	positionTransitionMs,
	isScopedPanelSpotlight = false,
	suppressStopTooltips = false,
}: {
	cell: HexagonStopCellModel;
	hoveredHex: { row: number; col: number } | null;
	focusedPathIndex: number;
	demoMode: boolean;
	pathStopIcons: Record<string, string>;
	renderPathStopTooltip?: (ctx: PathStopRenderContext) => ReactNode;
	highlightedStopId?: string | null;
	stopButtonRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
	handlePathStopClick: (stopId: string) => void;
	isPathIndexVisible: (pathIndex: number) => boolean;
	activePlayerCoord: { row: number; col: number } | null;
	positionTransitionMs: number;
	isScopedPanelSpotlight?: boolean;
	suppressStopTooltips?: boolean;
}) {
	const stopOverlaySize = "var(--stop-overlay-size)";
	const isFocused =
		cell.focusOrderIndex >= 0 && cell.focusOrderIndex === focusedPathIndex;
	const isHovered =
		hoveredHex?.row === cell.row && hoveredHex?.col === cell.col;
	const overlayScale =
		isHovered && cell.isUnlocked ? HEXAGON_MAP_HOVER_SCALE : 1;
	const isVisible = isPathIndexVisible(cell.pathIndex);
	const isCharacterOnTile =
		activePlayerCoord != null &&
		activePlayerCoord.row === cell.row &&
		activePlayerCoord.col === cell.col;
	const stopIconOpacity = !isVisible
		? 0
		: isCharacterOnTile
			? CHARACTER_ON_STOP_ICON_OPACITY
			: isScopedPanelSpotlight
				? SCOPED_PANEL_SPOTLIGHT_PATH_TILE_DIMMED_OPACITY
				: !cell.isUnlocked
					? LOCKED_PATH_TILE_OPACITY
					: 1;
	const isTooltipOpen = !suppressStopTooltips && (isHovered || isFocused);
	const tooltipBody = demoMode ? (
		<HexagonMapDemoPathStopTooltip
			title={cell.stopId ?? cell.iconKey}
			description="Demo stop"
		/>
	) : cell.stopId ? (
		(renderPathStopTooltip?.({
			stopId: cell.stopId,
			row: cell.row,
			col: cell.col,
			isUnlocked: cell.isUnlocked,
			isCompleted: cell.isCompleted,
			isHighlighted: highlightedStopId === cell.stopId,
			iconSrc: pathStopIcons[cell.iconKey] ?? cell.iconSrc,
		}) ?? null)
	) : null;
	const refKey = cell.stopId ?? cell.iconKey;
	const useTransparentTooltipChrome = !demoMode && Boolean(cell.stopId);
	const canShowTooltip = tooltipBody != null && !suppressStopTooltips;

	/**
	 * Full hex footprint (not just the icon) is the tooltip reference + click target.
	 * Map hex hover (`hoveredHex`) owns open — ContentTooltip is controlled so
	 * remounting / PE wrapper / tip dismiss cannot flicker the icon.
	 */
	const stopButton = (
		<button
			ref={(node) => {
				if (node) {
					stopButtonRefs.current.set(refKey, node);
				} else {
					stopButtonRefs.current.delete(refKey);
				}
			}}
			type="button"
			tabIndex={suppressStopTooltips ? -1 : isFocused ? 0 : -1}
			{...(isScopedPanelSpotlight
				? { [SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]: "stop-icon" }
				: {})}
			className={cn(
				"absolute flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2",
				isScopedPanelSpotlight ? "z-[56]" : "z-30",
				isScopedPanelSpotlight && scopedPanelSpotlightClassName,
				cell.isUnlocked ? "cursor-pointer" : "cursor-not-allowed",
			)}
			style={{
				left: cell.left,
				top: cell.top,
				width: "var(--hexagon-width)",
				height: "var(--hexagon-height)",
				opacity: stopIconOpacity,
				transform: `translate(-50%, -50%) scale(${isVisible ? overlayScale : 0})`,
				transition: isScopedPanelSpotlight
					? `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), opacity ${HEX_PATH_TILE_TRAIL_SCALE_OUT_MS}ms ease, transform 0.25s cubic-bezier(.4,2,.35,1)`
					: `left ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), top ${positionTransitionMs}ms cubic-bezier(.22,1,.36,1), opacity 0.25s cubic-bezier(.4,2,.35,1), transform 0.25s cubic-bezier(.4,2,.35,1)`,
				...(isScopedPanelSpotlight
					? {
							zIndex: SCOPED_PANEL_MAP_SPOTLIGHT_STOP_ICON_Z_INDEX,
						}
					: {}),
			}}
			onClick={() => {
				if (cell.stopId) {
					handlePathStopClick(cell.stopId);
				}
			}}
			disabled={!cell.isUnlocked}
			aria-label={cell.stopId ? `Stop ${cell.stopId}` : "Map marker"}
			data-hexagon-stop-tile=""
		>
			<span
				aria-hidden
				className="pointer-events-none relative block"
				style={{
					width: stopOverlaySize,
					height: stopOverlaySize,
				}}
			>
				<Image
					src={cell.iconSrc}
					alt=""
					fill
					sizes="64px"
					className="object-contain"
				/>
			</span>
		</button>
	);

	if (!canShowTooltip) {
		return stopButton;
	}

	return (
		<ContentTooltip
			asChild
			{...(useTransparentTooltipChrome
				? CONTENT_TOOLTIP_ABILITY_DARK
				: CONTENT_TOOLTIP_SHORT)}
			openOn="controlled"
			open={isTooltipOpen}
			side={cell.tooltipPlacement.side}
			align={cell.tooltipPlacement.align}
			sideOffset={10}
			content={tooltipBody}
		>
			{stopButton}
		</ContentTooltip>
	);
});

const HexagonStopLayer = memo(function HexagonStopLayer({
	stopCells,
	hoveredHex,
	focusedPathIndex,
	demoMode,
	pathStopIcons,
	renderPathStopTooltip,
	highlightedStopId,
	stopButtonRefs,
	handlePathStopClick,
	isPathIndexVisible,
	activePlayerCoord,
	positionTransitionMs,
	scopedPanelSpotlightRenderCoordKeys,
	suppressStopTooltips = false,
	layerPass = "full",
}: {
	stopCells: HexagonStopCellModel[];
	hoveredHex: { row: number; col: number } | null;
	focusedPathIndex: number;
	demoMode: boolean;
	pathStopIcons: Record<string, string>;
	renderPathStopTooltip?: (ctx: PathStopRenderContext) => ReactNode;
	highlightedStopId?: string | null;
	stopButtonRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
	handlePathStopClick: (stopId: string) => void;
	isPathIndexVisible: (pathIndex: number) => boolean;
	activePlayerCoord: { row: number; col: number } | null;
	positionTransitionMs: number;
	scopedPanelSpotlightRenderCoordKeys: ReadonlySet<string>;
	suppressStopTooltips?: boolean;
	layerPass?: ScopedPanelMapLayerPass;
}) {
	return stopCells.map((cell) => {
		const isScopedPanelSpotlight = scopedPanelSpotlightRenderCoordKeys.has(
			coordKey(cell.row, cell.col),
		);
		if (
			!shouldRenderScopedPanelMapLayerPass(
				layerPass,
				isScopedPanelSpotlight,
			)
		) {
			return null;
		}
		return (
			<HexagonStopMarker
				key={cell.nodeId}
				cell={cell}
				hoveredHex={hoveredHex}
				focusedPathIndex={focusedPathIndex}
				demoMode={demoMode}
				pathStopIcons={pathStopIcons}
				renderPathStopTooltip={renderPathStopTooltip}
				highlightedStopId={highlightedStopId}
				stopButtonRefs={stopButtonRefs}
				handlePathStopClick={handlePathStopClick}
				isPathIndexVisible={isPathIndexVisible}
				activePlayerCoord={activePlayerCoord}
				positionTransitionMs={positionTransitionMs}
				isScopedPanelSpotlight={isScopedPanelSpotlight}
				suppressStopTooltips={suppressStopTooltips}
			/>
		);
	});
});

function buildLinearCompatibilityLayout(
	graph: ModuleMapGraph,
	layoutOptions?: Omit<ComputeMapLayoutOptions, "seed">,
): MapLayout {
	const padding = resolveMapPathPadding(layoutOptions?.pathPadding);
	const direction = layoutOptions?.direction ?? "bottom-top";
	const pathCells: PathCell[] = [];
	const nodeById = new Map<string, { row: number; col: number }>();
	const pathCoordSet = new Set<string>();
	const spinePath: Array<{ row: number; col: number }> = [];
	const isVertical = direction === "bottom-top" || direction === "top-bottom";
	const length = Math.max(1, graph.nodes.length);
	const rows = isVertical ? length + padding.rows * 2 : padding.rows * 2 + 1;
	const cols = isVertical ? padding.cols * 2 + 1 : length + padding.cols * 2;
	const fixedRow = padding.rows;
	const fixedCol = padding.cols;

	for (let index = 0; index < graph.nodes.length; index += 1) {
		const node = graph.nodes[index];
		if (!node) {
			continue;
		}
		const coord =
			direction === "top-bottom"
				? { row: padding.rows + index, col: fixedCol }
				: direction === "left-right"
					? { row: fixedRow, col: padding.cols + index }
					: direction === "right-left"
						? {
								row: fixedRow,
								col: cols - 1 - padding.cols - index,
							}
						: {
								row: rows - 1 - padding.rows - index,
								col: fixedCol,
							};
		pathCells.push({
			row: coord.row,
			col: coord.col,
			nodeId: node.id,
			pathIndex: index,
			branchCorridorId: "branchId" in node ? node.branchId : undefined,
			branchRejoins:
				"branchRejoins" in node ? node.branchRejoins : undefined,
		});
		nodeById.set(node.id, coord);
		pathCoordSet.add(coordKey(coord.row, coord.col));
		spinePath.push(coord);
	}

	const startCell = spinePath[0] ?? { row: fixedRow, col: fixedCol };
	const endCell = spinePath[spinePath.length - 1] ?? startCell;

	return {
		rows,
		cols,
		spinePath,
		pathCoordSet,
		pathCells,
		nodeById,
		branchCorridors: graph.branchCorridorIds,
		branchCorridorIdByCoordKey: new Map(
			[...pathCoordSet].map((key) => [key, null]),
		),
		startCell,
		endCell,
		pathPadding: padding,
		direction,
		generationAttempt: 0,
	};
}

export function HexagonMap({
	graph,
	layoutSeed,
	layoutSeedMode = "exact",
	layoutOptions,
	backdropJpg,
	environmentImages,
	pathStopIcons,
	pathStopSpeechBubbles = {},
	pathStopBadges,
	characterWalksToStops = true,
	unlockedStopIds,
	completedStopIds = new Set(),
	playerStopId = null,
	playerPathPosition = null,
	onPlayerPathPositionChange,
	playerIcons,
	renderPlayerCharacter,
	highlightedStopId,
	demoMode = false,
	isTranslucent = true,
	showAuthoringControls = false,
	onResolvedLayoutSeedChange,
	onLayoutGenerationFailed,
	onPathStopPress,
	onMapPathSelectionChange,
	requestedNavigateStopId = null,
	onRequestedNavigateStopHandled,
	onKeyboardDismiss,
	renderPathStopTooltip,
	gridAnimationMode = "wipe",
	gridAnimationDurationMs = GRID_ANIMATION_DURATION_MS,
	gridAnimationTotalDurationMs = GRID_ANIMATION_TOTAL_DURATION_MS,
	environmentAnimationTotalDurationMs = gridAnimationTotalDurationMs,
	initialAnimationEnabled = true,
	onInitialStagePreloadReady,
	initialStagePreloadNonce,
	pathTileOuterCornerRadiusPx = HEXAGON_PATH_TILE_DEFAULT_OUTER_CORNER_RADIUS_PX,
	pathTileBorderWidthPx,
	characterMovementMsPerTile = DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE,
	playerAvatarSrc = null,
	playerPinSrc = null,
	layoutErrorActions,
	scopedPanelSpotlightStopId = null,
	scopedPanelPreviewSpeechBubble = null,
	onScopedPanelDismiss,
	onScopedPanelNavAvailabilityChange,
	requestedViewportFocus = null,
	scopedPanelNavigationToken = 0,
	scopedPanelSheetInsets = null,
	previewerChrome = null,
	onMapViewportElement,
	topLeftOctagonButton,
	topRightOctagonButton,
	showThumbMovementControls = false,
	className,
}: HexagonMapProps) {
	const mapStageRef = useRef<HTMLDivElement>(null);
	const contentStageRef = useRef<HTMLDivElement>(null);
	const pathClickStartRef = useRef<{
		clientX: number;
		clientY: number;
	} | null>(null);
	const stopButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const hoverFrameRef = useRef<number | null>(null);
	const hoveredHexRef = useRef<{ row: number; col: number } | null>(null);
	const lastPointerHoverAffectedKeysRef = useRef<Set<string>>(new Set());
	const hoverCellRootsRef = useRef(new Map<string, HTMLElement>());
	const scopedPanelSpotlightStopIdRef = useRef<string | null>(
		scopedPanelSpotlightStopId ?? null,
	);
	scopedPanelSpotlightStopIdRef.current = scopedPanelSpotlightStopId ?? null;
	const scopedPanelSheetInsetsRef = useRef(scopedPanelSheetInsets);
	scopedPanelSheetInsetsRef.current = scopedPanelSheetInsets;
	const lastKnownScopedPanelSheetInsetsRef = useRef(scopedPanelSheetInsets);
	if (scopedPanelSheetInsets != null) {
		lastKnownScopedPanelSheetInsetsRef.current = scopedPanelSheetInsets;
	}
	const previousScopedPanelSheetInsetsKeyRef = useRef<string | null>(null);
	/** Blocks resize layout recenter from snapping mid preview pan / character walk. */
	const scopedPanelLayoutRecenterSuppressUntilRef = useRef(0);
	const activePlayerCoordRef = useRef<{ row: number; col: number } | null>(
		null,
	);
	const activeCharacterKeyForVisualsRef = useRef<string | null>(null);
	const prevActivePlayerCoordRef = useRef<{
		row: number;
		col: number;
	} | null>(null);
	const lastResolvedSeedNotificationKeyRef = useRef<string | null>(null);
	const lastInitialStagePreloadReadyKeyRef = useRef<string | null>(null);
	const reducedMotion = useReducedMotion();
	const [hoveredHex, setHoveredHex] = useState<{
		row: number;
		col: number;
	} | null>(null);
	const [focusedPathIndex, setFocusedPathIndex] = useState(-1);

	const graphLayoutKey = useMemo(() => serializeLayoutGraph(graph), [graph]);
	const layoutOptionsKey = serializeLayoutOptions(layoutOptions);
	const resolvedLayoutCacheKey = `${layoutSeedMode}::${layoutSeed}::${graphLayoutKey}::${layoutOptionsKey}`;

	const layoutResult = useMemo(() => {
		const cached = getCachedResolvedLayout(resolvedLayoutCacheKey);
		if (cached) {
			return { layout: cached, error: null };
		}
		try {
			return {
				layout: setCachedResolvedLayout(
					resolvedLayoutCacheKey,
					resolveHexagonMapLayout({
						graph,
						layoutSeed,
						layoutSeedMode,
						layoutOptions,
					}),
				),
				error: null,
			};
		} catch (error) {
			return { layout: null, error };
		}
	}, [
		graph,
		layoutOptions,
		layoutSeed,
		layoutSeedMode,
		resolvedLayoutCacheKey,
	]);

	useEffect(() => {
		if (!layoutResult.layout) {
			lastResolvedSeedNotificationKeyRef.current = null;
			return;
		}
		const notificationKey = `${resolvedLayoutCacheKey}::${layoutResult.layout.resolvedSeed}`;
		if (lastResolvedSeedNotificationKeyRef.current === notificationKey) {
			return;
		}
		lastResolvedSeedNotificationKeyRef.current = notificationKey;
		onResolvedLayoutSeedChange?.(layoutResult.layout.resolvedSeed);
	}, [
		layoutResult.layout,
		onResolvedLayoutSeedChange,
		resolvedLayoutCacheKey,
	]);

	useEffect(() => {
		if (layoutResult.layout || !layoutResult.error) {
			return;
		}
		onLayoutGenerationFailed?.();
	}, [layoutResult.error, layoutResult.layout, onLayoutGenerationFailed]);
	const fallbackLayout = useMemo(
		() => buildLinearCompatibilityLayout(graph, layoutOptions),
		[graph, layoutOptions],
	);

	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
	const containerSizeRef = useRef(containerSize);
	containerSizeRef.current = containerSize;

	const resolvedLayoutSeed = layoutResult.layout?.resolvedSeed ?? layoutSeed;
	const resolvedLayout = layoutResult.layout?.layout ?? fallbackLayout;
	const layoutRows = resolvedLayout.rows;
	const pathDirection = resolvedLayout.direction ?? "bottom-top";
	const fitAxis: HexagonMapFitAxis =
		pathDirection === "left-right" || pathDirection === "right-left"
			? "width"
			: "height";

	const {
		displayRows: displayRowsTarget,
		displayCols: displayColsTarget,
		metrics,
	} = useMemo(
		() =>
			resolveHexagonMapDisplayMetrics(
				layoutRows,
				resolvedLayout.cols,
				containerSize.width,
				containerSize.height,
				fitAxis,
			),
		[
			containerSize.height,
			containerSize.width,
			fitAxis,
			layoutRows,
			resolvedLayout.cols,
		],
	);

	const displayLayout = useMemo(
		() =>
			shiftLayoutRows(
				shiftLayoutColumns(resolvedLayout, displayColsTarget),
				displayRowsTarget,
			),
		[displayColsTarget, displayRowsTarget, resolvedLayout],
	);

	const displayRows = displayLayout.rows;
	const displayCols = displayLayout.cols;
	const logicalLayoutIdentity = `${resolvedLayoutCacheKey}:${resolvedLayoutSeed}`;
	const [layoutScopedPlayerPathPosition, setLayoutScopedPlayerPathPosition] =
		useState<MapPlayerPathPosition | null>(() =>
			showAuthoringControls ? null : playerPathPosition,
		);
	const lastLayoutIdentityForRestoreRef = useRef(logicalLayoutIdentity);
	const lastStablePlayerDisplayCenterRef = useRef<{
		left: number;
		top: number;
	} | null>(null);
	const [layoutRestoreVisualCenter, setLayoutRestoreVisualCenter] = useState<{
		left: number;
		top: number;
	} | null>(null);
	const [characterLayoutMotionPhase, setCharacterLayoutMotionPhase] =
		useState<"stable" | "exit" | "enter">("stable");
	const characterLayoutMotionTimerRef = useRef<ReturnType<
		typeof setTimeout
	> | null>(null);

	useEffect(() => {
		if (showAuthoringControls) {
			return;
		}
		setLayoutScopedPlayerPathPosition(playerPathPosition);
	}, [playerPathPosition, showAuthoringControls]);
	const displayRowOffset =
		displayLayout.startCell.row - resolvedLayout.startCell.row;
	const displayColOffset =
		displayLayout.startCell.col - resolvedLayout.startCell.col;
	const previousDisplayFrameRef = useRef<{
		logicalLayoutIdentity: string;
		containerWidth: number;
		containerHeight: number;
		displayRows: number;
		displayCols: number;
	} | null>(null);
	const previousDisplayFrame = previousDisplayFrameRef.current;
	const isResizeOnlyDisplayChange =
		previousDisplayFrame != null &&
		previousDisplayFrame.logicalLayoutIdentity === logicalLayoutIdentity &&
		(previousDisplayFrame.containerWidth !== containerSize.width ||
			previousDisplayFrame.containerHeight !== containerSize.height ||
			previousDisplayFrame.displayRows !== displayRows ||
			previousDisplayFrame.displayCols !== displayCols);
	const contentPositionTransitionMs = isResizeOnlyDisplayChange
		? 0
		: gridAnimationDurationMs;

	const contentBounds = useMemo(
		() => computeHexagonMapContentBounds(displayRows, displayCols, metrics),
		[displayCols, displayRows, metrics],
	);

	const fitScale = useMemo(() => {
		if (containerSize.width <= 0 || containerSize.height <= 0) {
			return 1;
		}
		const scaleX = containerSize.width / Math.max(contentBounds.width, 1);
		const scaleY = containerSize.height / Math.max(contentBounds.height, 1);
		return fitAxis === "width" ? scaleX : scaleY;
	}, [
		containerSize.height,
		containerSize.width,
		contentBounds.height,
		contentBounds.width,
		fitAxis,
	]);
	const {
		zoom: userZoom,
		scale,
		pan,
		zoomStep: userZoomStep,
		setZoom: setUserZoom,
		contentStyle: panZoomContentStyle,
		setViewportRef: setPanZoomViewportRef,
		setContentRef: setPanZoomContentRef,
		panToCenterContentPoint,
		viewportSize: panZoomViewportSize,
		panAndZoomToCenterContentPoint,
	} = usePanZoomSurface({
		contentWidth: contentBounds.width,
		contentHeight: contentBounds.height,
		baseScale: fitScale,
		minZoom: HEXAGON_MAP_MIN_USER_ZOOM,
		maxZoom: HEXAGON_MAP_MAX_USER_ZOOM,
		zoomStep: HEXAGON_MAP_ZOOM_BUTTON_STEP,
		reducedMotion: reducedMotion ?? false,
		transitionDurationMs: 200,
		interactionsEnabled: scopedPanelSpotlightStopId == null,
	});

	useEffect(() => {
		const el = mapStageRef.current;
		if (!el) {
			return;
		}
		const update = () => {
			setContainerSize((prev) => {
				const next = {
					width: el.clientWidth,
					height: el.clientHeight,
				};
				if (prev.width === next.width && prev.height === next.height) {
					return prev;
				}
				return next;
			});
		};
		update();
		const observer = new ResizeObserver(() => update());
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		previousDisplayFrameRef.current = {
			logicalLayoutIdentity,
			containerWidth: containerSize.width,
			containerHeight: containerSize.height,
			displayRows,
			displayCols,
		};
	}, [
		containerSize.height,
		containerSize.width,
		displayCols,
		displayRows,
		logicalLayoutIdentity,
	]);

	const pathFocusOrder = useMemo(
		() =>
			buildPathFocusOrder(graph, displayLayout).map((item) => ({
				pathIndex: item.pathIndex,
				stopId: item.stopId,
			})),
		[graph, displayLayout],
	);
	const focusOrderIndexByStopId = useMemo(() => {
		const map = new Map<string, number>();
		for (let index = 0; index < pathFocusOrder.length; index += 1) {
			const item = pathFocusOrder[index];
			if (item?.stopId) {
				map.set(item.stopId, index);
			}
		}
		return map;
	}, [pathFocusOrder]);

	const completedPathCoordKeys = useMemo(
		() =>
			buildCompletedPathCoordKeys(graph, displayLayout, completedStopIds),
		[completedStopIds, displayLayout, graph],
	);

	const spineIndexByCoord = useMemo(() => {
		const map = new Map<string, number>();
		for (let i = 0; i < displayLayout.spinePath.length; i++) {
			const p = displayLayout.spinePath[i];
			if (p) {
				map.set(coordKey(p.row, p.col), i);
			}
		}
		return map;
	}, [displayLayout.spinePath]);

	const pathLength =
		displayLayout.pathCells.reduce((maxIndex, cell) => {
			return Math.max(maxIndex, cell.pathIndex);
		}, -1) + 1;
	const pathCellByCoord = useMemo(() => {
		const map = new Map<string, PathCell>();
		for (const cell of displayLayout.pathCells) {
			map.set(coordKey(cell.row, cell.col), cell);
		}
		return map;
	}, [displayLayout.pathCells]);

	const gridCells = useMemo<HexagonGridCellModel[]>(() => {
		const startedAt =
			typeof performance !== "undefined" ? performance.now() : Date.now();
		const cells: HexagonGridCellModel[] = [];
		for (let row = 0; row < displayRows; row += 1) {
			for (let col = 0; col < displayCols; col += 1) {
				const key = coordKey(row, col);
				const pathCell = pathCellByCoord.get(key);
				const stopId = pathCell?.nodeId?.startsWith("sq:map:stop:")
					? pathCell.nodeId.replace("sq:map:stop:", "")
					: null;
				const pos = hexCellPosition(row, col, metrics);
				cells.push({
					key,
					row,
					col,
					left: pos.left,
					top: pos.top,
					isPath: displayLayout.pathCoordSet.has(key),
					pathIndex:
						pathCell?.pathIndex ?? spineIndexByCoord.get(key) ?? -1,
					isCompleted:
						pathCell != null && completedPathCoordKeys.has(key),
					stopId,
				});
			}
		}
		const elapsedMs =
			(typeof performance !== "undefined"
				? performance.now()
				: Date.now()) - startedAt;
		if (elapsedMs >= 8) {
			logHexagonMapPerfEvent("grid cells computed", {
				rows: displayRows,
				displayCols,
				gridCellCount: cells.length,
				elapsedMs: Math.round(elapsedMs),
			});
		}
		return cells;
	}, [
		displayCols,
		displayRows,
		displayLayout.pathCoordSet,
		metrics,
		completedPathCoordKeys,
		pathCellByCoord,
		spineIndexByCoord,
	]);
	const pathIndexByCoordKey = useMemo(() => {
		const map = new Map<string, number>();
		for (const cell of gridCells) {
			if (cell.isPath && cell.pathIndex >= 0) {
				map.set(cell.key, cell.pathIndex);
			}
		}
		return map;
	}, [gridCells]);
	const pathProgressByCoordKey = useMemo(
		() =>
			buildPathProgressByCoordKey(
				displayLayout.pathCoordSet,
				displayLayout.startCell,
			),
		[displayLayout.pathCoordSet, displayLayout.startCell],
	);
	const branchCorridorIdByCoordKey = useMemo(
		() => buildBranchCorridorIdByCoordKeyForLayout(displayLayout),
		[displayLayout],
	);
	const furthestUnlockedPathProgress = useMemo(
		() =>
			resolveFurthestUnlockedPathProgress(
				displayLayout.pathCells,
				unlockedStopIds,
				pathProgressByCoordKey,
			),
		[displayLayout.pathCells, pathProgressByCoordKey, unlockedStopIds],
	);
	const furthestUnlockedStopOnPath = useMemo(
		() =>
			resolveFurthestUnlockedStopOnPath(
				displayLayout.pathCells,
				unlockedStopIds,
				pathProgressByCoordKey,
			),
		[displayLayout.pathCells, pathProgressByCoordKey, unlockedStopIds],
	);
	const lockedPathCoordKeys = useMemo(
		() => buildLockedPathCoordKeys(graph, displayLayout, unlockedStopIds),
		[graph, displayLayout, unlockedStopIds],
	);
	const lockedPathCoordKeysRef = useRef(lockedPathCoordKeys);
	lockedPathCoordKeysRef.current = lockedPathCoordKeys;
	const lockedSpacerPathCoordKeys = useMemo(() => {
		const stopCoordKeys = new Set<string>();
		for (const cell of displayLayout.pathCells) {
			if (cell.nodeId?.startsWith("sq:map:stop:")) {
				stopCoordKeys.add(coordKey(cell.row, cell.col));
			}
		}
		const spacerKeys = new Set<string>();
		for (const key of lockedPathCoordKeys) {
			if (!stopCoordKeys.has(key)) {
				spacerKeys.add(key);
			}
		}
		return spacerKeys;
	}, [displayLayout.pathCells, lockedPathCoordKeys]);
	const isHoveredLockedSpacerPathTile = useMemo(() => {
		if (!hoveredHex) {
			return false;
		}
		return lockedSpacerPathCoordKeys.has(
			coordKey(hoveredHex.row, hoveredHex.col),
		);
	}, [hoveredHex, lockedSpacerPathCoordKeys]);
	const initialGridAnimationLeadDelayMs = reducedMotion
		? 0
		: resolveGridAnimationCycleDurationMs({
				gridAnimationDurationMs,
				gridAnimationTotalDurationMs,
			});
	const [renderGridCells, setRenderGridCells] = useState<
		AnimatedHexagonGridCellModel[]
	>(() =>
		reducedMotion
			? buildAnimatedGridCells({
					cells: gridCells,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: gridAnimationTotalDurationMs,
					phase: "stable",
				})
			: !initialAnimationEnabled
				? []
				: buildAnimatedGridCells({
						cells: gridCells,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: displayRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: gridAnimationTotalDurationMs,
						phase: "enter",
					}),
	);
	const previousGridCellsRef = useRef<HexagonGridCellModel[] | null>(
		gridCells,
	);
	const hasInitializedGridAnimationRef = useRef(false);
	const gridAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const pathOverlayCells = useMemo<AnimatedHexagonGridCellModel[]>(
		() =>
			gridCells
				.filter((cell) => cell.isPath)
				.map((cell) => ({
					...cell,
					animationDelayMs: 0,
					animationPhase: "stable",
				})),
		[gridCells],
	);
	const overscanGridCells = useMemo<AnimatedHexagonGridCellModel[]>(() => {
		const cells: AnimatedHexagonGridCellModel[] = [];
		for (
			let row = -HEXAGON_MAP_GRID_OVERSCAN;
			row < displayRows + HEXAGON_MAP_GRID_OVERSCAN;
			row += 1
		) {
			for (
				let col = -HEXAGON_MAP_GRID_OVERSCAN;
				col < displayCols + HEXAGON_MAP_GRID_OVERSCAN;
				col += 1
			) {
				if (
					row >= 0 &&
					row < displayRows &&
					col >= 0 &&
					col < displayCols
				) {
					continue;
				}
				const pos = hexCellPosition(row, col, metrics);
				cells.push({
					key: `overscan:${coordKey(row, col)}`,
					row,
					col,
					left: pos.left,
					top: pos.top,
					isPath: false,
					pathIndex: -1,
					isCompleted: false,
					stopId: null,
					animationDelayMs: 0,
					animationPhase: "stable",
				});
			}
		}
		return cells;
	}, [displayCols, displayRows, metrics]);
	const visualGridCells = useMemo(
		() => [...renderGridCells, ...overscanGridCells],
		[overscanGridCells, renderGridCells],
	);

	const environmentPlacements = useMemo(() => {
		if (!environmentImages?.length) {
			return [];
		}
		const startedAt =
			typeof performance !== "undefined" ? performance.now() : Date.now();
		const rng = createSeededRng(
			`${resolvedLayoutSeed}:${graphLayoutKey}:environment`,
		);
		const logicalPlacements = placeEnvironmentImages({
			configs: environmentImages,
			rows: layoutRows,
			cols: resolvedLayout.cols,
			occupied: new Set(resolvedLayout.pathCoordSet),
			rng,
		});
		const placements = logicalPlacements.map((placement) => ({
			...placement,
			row: placement.row + displayRowOffset,
			col: placement.col + displayColOffset,
		}));
		const elapsedMs =
			(typeof performance !== "undefined"
				? performance.now()
				: Date.now()) - startedAt;
		if (elapsedMs >= 8) {
			logHexagonMapPerfEvent("environment placement computed", {
				rows: layoutRows,
				displayCols: resolvedLayout.cols,
				environmentCount: placements.length,
				elapsedMs: Math.round(elapsedMs),
			});
		}
		return placements;
	}, [
		displayColOffset,
		displayRowOffset,
		environmentImages,
		graphLayoutKey,
		layoutRows,
		resolvedLayoutSeed,
		resolvedLayout.cols,
		resolvedLayout.pathCoordSet,
	]);
	const environmentPlacementModels = useMemo<
		EnvironmentPlacementModel[]
	>(() => {
		return environmentPlacements.map((placement) => {
			const footprintBounds = computeEnvironmentFootprintPixelBounds({
				anchorRow: placement.row,
				anchorCol: placement.col,
				footprint: {
					width: placement.footprintWidth,
					height: placement.footprintHeight,
				},
				metrics,
			});
			const spriteDimensions = resolveEnvironmentSpriteDimensions({
				bounds: footprintBounds,
				aspectRatio: placement.aspectRatio,
				minSize: Math.max(28, Math.round(metrics.hexagonWidth * 0.96)),
			});
			return {
				...placement,
				key: `${placement.src}:${placement.row - displayRowOffset}:${placement.col - displayColOffset}:${placement.footprintWidth}x${placement.footprintHeight}:${placement.flipHorizontal ? "flip" : "plain"}`,
				left: footprintBounds.centerX,
				top: footprintBounds.centerY,
				width: spriteDimensions.width,
				height: spriteDimensions.height,
				animationDelayMs: 0,
				animationPhase: "stable",
			};
		});
	}, [displayColOffset, displayRowOffset, environmentPlacements, metrics]);
	const [renderEnvironmentPlacements, setRenderEnvironmentPlacements] =
		useState<EnvironmentPlacementModel[]>(() =>
			reducedMotion
				? buildAnimatedEnvironmentPlacements({
						placements: environmentPlacementModels,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: displayRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: environmentAnimationTotalDurationMs,
						phase: "stable",
					})
				: !initialAnimationEnabled
					? []
					: buildAnimatedEnvironmentPlacements({
							placements: environmentPlacementModels,
							layoutSeed: resolvedLayoutSeed,
							mode: gridAnimationMode,
							rows: displayRows,
							animationDurationMs: gridAnimationDurationMs,
							totalDurationMs:
								environmentAnimationTotalDurationMs,
							phase: "enter",
							baseDelayMs: initialGridAnimationLeadDelayMs,
						}),
		);
	const previousEnvironmentPlacementsRef = useRef<
		EnvironmentPlacementModel[] | null
	>(environmentPlacementModels);
	const hasInitializedEnvironmentAnimationRef = useRef(false);
	const environmentExitTimerRef = useRef<ReturnType<
		typeof setTimeout
	> | null>(null);
	const environmentEnterTimerRef = useRef<ReturnType<
		typeof setTimeout
	> | null>(null);

	useEffect(() => {
		if (initialAnimationEnabled) {
			return;
		}
		if (
			containerSize.width <= 0 ||
			containerSize.height <= 0 ||
			gridCells.length === 0
		) {
			return;
		}
		const preloadReadyKey = `${resolvedLayoutCacheKey}:${resolvedLayoutSeed}:${containerSize.width}x${containerSize.height}:${gridCells.length}:${initialStagePreloadNonce ?? ""}`;
		if (lastInitialStagePreloadReadyKeyRef.current === preloadReadyKey) {
			return;
		}
		lastInitialStagePreloadReadyKeyRef.current = preloadReadyKey;
		onInitialStagePreloadReady?.();
	}, [
		containerSize.height,
		containerSize.width,
		gridCells.length,
		initialAnimationEnabled,
		initialStagePreloadNonce,
		onInitialStagePreloadReady,
		resolvedLayoutCacheKey,
		resolvedLayoutSeed,
	]);

	useEffect(() => {
		if (gridAnimationTimerRef.current) {
			clearTimeout(gridAnimationTimerRef.current);
			gridAnimationTimerRef.current = null;
		}
		if (reducedMotion) {
			setRenderGridCells(
				buildAnimatedGridCells({
					cells: gridCells,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: gridAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			previousGridCellsRef.current = gridCells;
			hasInitializedGridAnimationRef.current = true;
			return;
		}
		if (!initialAnimationEnabled) {
			setRenderGridCells([]);
			previousGridCellsRef.current = gridCells;
			hasInitializedGridAnimationRef.current = false;
			return;
		}
		if (!hasInitializedGridAnimationRef.current) {
			hasInitializedGridAnimationRef.current = true;
			previousGridCellsRef.current = gridCells;
			setRenderGridCells(
				buildAnimatedGridCells({
					cells: gridCells,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: gridAnimationTotalDurationMs,
					phase: "enter",
				}),
			);
			gridAnimationTimerRef.current = setTimeout(() => {
				setRenderGridCells(
					buildAnimatedGridCells({
						cells: gridCells,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: displayRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: gridAnimationTotalDurationMs,
						phase: "stable",
					}),
				);
			}, initialGridAnimationLeadDelayMs);
			return;
		}
		if (isResizeOnlyDisplayChange) {
			setRenderGridCells(
				buildAnimatedGridCells({
					cells: gridCells,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: gridAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			previousGridCellsRef.current = gridCells;
			return;
		}
		const gridCellDelta = resolveKeyedAnimationDelta({
			previousItems: previousGridCellsRef.current,
			nextItems: gridCells,
		});
		previousGridCellsRef.current = gridCells;
		if (!gridCellDelta.hasDelta) {
			setRenderGridCells(
				buildAnimatedGridCells({
					cells: gridCellDelta.sharedItems,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: gridAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			return;
		}
		setRenderGridCells([
			...buildAnimatedGridCells({
				cells: gridCellDelta.sharedItems,
				layoutSeed: resolvedLayoutSeed,
				mode: gridAnimationMode,
				rows: gridCellDelta.animationRows,
				animationDurationMs: gridAnimationDurationMs,
				totalDurationMs: gridAnimationTotalDurationMs,
				phase: "stable",
			}),
			...buildAnimatedGridCells({
				cells: gridCellDelta.enteringItems,
				layoutSeed: resolvedLayoutSeed,
				mode: gridAnimationMode,
				rows: gridCellDelta.animationRows,
				animationDurationMs: gridAnimationDurationMs,
				totalDurationMs: gridAnimationTotalDurationMs,
				phase: "enter",
			}),
			...buildAnimatedGridCells({
				cells: gridCellDelta.exitingItems,
				layoutSeed: `${resolvedLayoutSeed}:previous-grid`,
				mode: gridAnimationMode,
				rows: gridCellDelta.animationRows,
				animationDurationMs: gridAnimationDurationMs,
				totalDurationMs: gridAnimationTotalDurationMs,
				phase: "exit",
			}),
		]);
		gridAnimationTimerRef.current = setTimeout(
			() => {
				setRenderGridCells(
					buildAnimatedGridCells({
						cells: gridCells,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: displayRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: gridAnimationTotalDurationMs,
						phase: "stable",
					}),
				);
			},
			resolveGridAnimationCycleDurationMs({
				gridAnimationDurationMs,
				gridAnimationTotalDurationMs,
			}),
		);
		return () => {
			if (gridAnimationTimerRef.current) {
				clearTimeout(gridAnimationTimerRef.current);
				gridAnimationTimerRef.current = null;
			}
		};
	}, [
		gridAnimationDurationMs,
		gridAnimationMode,
		gridAnimationTotalDurationMs,
		gridCells,
		initialAnimationEnabled,
		initialGridAnimationLeadDelayMs,
		isResizeOnlyDisplayChange,
		reducedMotion,
		resolvedLayoutSeed,
		displayRows,
	]);

	useEffect(() => {
		if (environmentExitTimerRef.current) {
			clearTimeout(environmentExitTimerRef.current);
			environmentExitTimerRef.current = null;
		}
		if (environmentEnterTimerRef.current) {
			clearTimeout(environmentEnterTimerRef.current);
			environmentEnterTimerRef.current = null;
		}
		if (reducedMotion) {
			setRenderEnvironmentPlacements(
				buildAnimatedEnvironmentPlacements({
					placements: environmentPlacementModels,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: environmentAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			previousEnvironmentPlacementsRef.current =
				environmentPlacementModels;
			hasInitializedEnvironmentAnimationRef.current = true;
			return;
		}
		if (!initialAnimationEnabled) {
			setRenderEnvironmentPlacements([]);
			previousEnvironmentPlacementsRef.current =
				environmentPlacementModels;
			hasInitializedEnvironmentAnimationRef.current = false;
			return;
		}
		if (!hasInitializedEnvironmentAnimationRef.current) {
			hasInitializedEnvironmentAnimationRef.current = true;
			previousEnvironmentPlacementsRef.current =
				environmentPlacementModels;
			setRenderEnvironmentPlacements(
				buildAnimatedEnvironmentPlacements({
					placements: environmentPlacementModels,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: environmentAnimationTotalDurationMs,
					phase: "enter",
					baseDelayMs: initialGridAnimationLeadDelayMs,
				}),
			);
			environmentEnterTimerRef.current = setTimeout(
				() => {
					setRenderEnvironmentPlacements(
						buildAnimatedEnvironmentPlacements({
							placements: environmentPlacementModels,
							layoutSeed: resolvedLayoutSeed,
							mode: gridAnimationMode,
							rows: displayRows,
							animationDurationMs: gridAnimationDurationMs,
							totalDurationMs:
								environmentAnimationTotalDurationMs,
							phase: "stable",
						}),
					);
				},
				initialGridAnimationLeadDelayMs +
					resolveGridAnimationCycleDurationMs({
						gridAnimationDurationMs,
						gridAnimationTotalDurationMs:
							environmentAnimationTotalDurationMs,
					}),
			);
			return;
		}
		if (isResizeOnlyDisplayChange) {
			setRenderEnvironmentPlacements(
				buildAnimatedEnvironmentPlacements({
					placements: environmentPlacementModels,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: environmentAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			previousEnvironmentPlacementsRef.current =
				environmentPlacementModels;
			return;
		}
		const previousEnvironmentPlacements =
			previousEnvironmentPlacementsRef.current ?? [];
		const environmentAnimationSequence = resolveKeyedAnimationSequence({
			previousItems: previousEnvironmentPlacements,
			nextItems: environmentPlacementModels,
		});
		previousEnvironmentPlacementsRef.current = environmentPlacementModels;
		if (!environmentAnimationSequence.delta.hasDelta) {
			setRenderEnvironmentPlacements(
				buildAnimatedEnvironmentPlacements({
					placements: environmentAnimationSequence.settledItems,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: environmentAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
			return;
		}
		const animationRows = environmentAnimationSequence.delta.animationRows;
		const environmentCycleDurationMs = resolveGridAnimationCycleDurationMs({
			gridAnimationDurationMs,
			gridAnimationTotalDurationMs: environmentAnimationTotalDurationMs,
		});
		const initialStableEnvironmentPlacements =
			buildAnimatedEnvironmentPlacements({
				placements:
					environmentAnimationSequence.initialFrame.stableItems,
				layoutSeed: resolvedLayoutSeed,
				mode: gridAnimationMode,
				rows: animationRows,
				animationDurationMs: gridAnimationDurationMs,
				totalDurationMs: environmentAnimationTotalDurationMs,
				phase: "stable",
			});
		setRenderEnvironmentPlacements([
			...initialStableEnvironmentPlacements,
			...buildAnimatedEnvironmentPlacements({
				placements:
					environmentAnimationSequence.initialFrame.animatedItems,
				layoutSeed:
					environmentAnimationSequence.initialFrame.phase === "exit"
						? `${resolvedLayoutSeed}:previous-environment`
						: resolvedLayoutSeed,
				mode: gridAnimationMode,
				rows: animationRows,
				animationDurationMs: gridAnimationDurationMs,
				totalDurationMs: environmentAnimationTotalDurationMs,
				phase: environmentAnimationSequence.initialFrame.phase,
			}),
		]);
		if (environmentAnimationSequence.followUpFrame) {
			const followUpFrame = environmentAnimationSequence.followUpFrame;
			environmentExitTimerRef.current = setTimeout(() => {
				const followUpStableEnvironmentPlacements =
					buildAnimatedEnvironmentPlacements({
						placements: followUpFrame.stableItems,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: animationRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: environmentAnimationTotalDurationMs,
						phase: "stable",
					});
				setRenderEnvironmentPlacements([
					...followUpStableEnvironmentPlacements,
					...buildAnimatedEnvironmentPlacements({
						placements: followUpFrame.animatedItems,
						layoutSeed: resolvedLayoutSeed,
						mode: gridAnimationMode,
						rows: animationRows,
						animationDurationMs: gridAnimationDurationMs,
						totalDurationMs: environmentAnimationTotalDurationMs,
						phase: followUpFrame.phase,
					}),
				]);
				environmentEnterTimerRef.current = setTimeout(() => {
					setRenderEnvironmentPlacements(
						buildAnimatedEnvironmentPlacements({
							placements:
								environmentAnimationSequence.settledItems,
							layoutSeed: resolvedLayoutSeed,
							mode: gridAnimationMode,
							rows: displayRows,
							animationDurationMs: gridAnimationDurationMs,
							totalDurationMs:
								environmentAnimationTotalDurationMs,
							phase: "stable",
						}),
					);
				}, environmentCycleDurationMs);
			}, environmentCycleDurationMs);
			return;
		}
		environmentEnterTimerRef.current = setTimeout(() => {
			setRenderEnvironmentPlacements(
				buildAnimatedEnvironmentPlacements({
					placements: environmentAnimationSequence.settledItems,
					layoutSeed: resolvedLayoutSeed,
					mode: gridAnimationMode,
					rows: displayRows,
					animationDurationMs: gridAnimationDurationMs,
					totalDurationMs: environmentAnimationTotalDurationMs,
					phase: "stable",
				}),
			);
		}, environmentCycleDurationMs);
		return () => {
			if (environmentExitTimerRef.current) {
				clearTimeout(environmentExitTimerRef.current);
				environmentExitTimerRef.current = null;
			}
			if (environmentEnterTimerRef.current) {
				clearTimeout(environmentEnterTimerRef.current);
				environmentEnterTimerRef.current = null;
			}
		};
	}, [
		environmentPlacementModels,
		gridAnimationDurationMs,
		gridAnimationMode,
		environmentAnimationTotalDurationMs,
		initialAnimationEnabled,
		initialGridAnimationLeadDelayMs,
		isResizeOnlyDisplayChange,
		reducedMotion,
		resolvedLayoutSeed,
		displayRows,
	]);

	const { isPathIndexVisible, revealedThroughIndex, isPathRevealComplete } =
		useHexagonMapAnimations({
			layoutSeed: resolvedLayoutSeed,
			pathLength,
			initialGridAnimationLeadDelayMs,
			animationEnabled: initialAnimationEnabled,
			reducedMotion: reducedMotion ?? false,
		});
	const visibleRowCount = displayRows;
	const isPathIndexVisibleForMovement = useCallback(
		(pathIndex: number) =>
			isPathRevealComplete || isPathIndexVisible(pathIndex),
		[isPathIndexVisible, isPathRevealComplete],
	);
	const isHoveredTraversablePathTile = useMemo(() => {
		if (!hoveredHex || scopedPanelSpotlightStopId != null) {
			return false;
		}
		const key = coordKey(hoveredHex.row, hoveredHex.col);
		if (
			!displayLayout.pathCoordSet.has(key) ||
			lockedPathCoordKeys.has(key)
		) {
			return false;
		}
		return isPathTileTraversalEnabled({
			row: hoveredHex.row,
			col: hoveredHex.col,
			pathProgress: pathProgressByCoordKey.get(key),
			pathCoordSet: displayLayout.pathCoordSet,
			furthestUnlockedPathProgress,
			isPathIndexVisible: isPathIndexVisibleForMovement,
			pathIndexForVisibility: pathIndexByCoordKey.get(key),
		});
	}, [
		displayLayout.pathCoordSet,
		furthestUnlockedPathProgress,
		hoveredHex,
		isPathIndexVisibleForMovement,
		lockedPathCoordKeys,
		pathIndexByCoordKey,
		pathProgressByCoordKey,
		scopedPanelSpotlightStopId,
	]);

	useHexagonMapPathKeyboard({
		containerRef: mapStageRef,
		pathFocusOrder,
		focusedIndex: focusedPathIndex,
		onFocusedIndexChange: setFocusedPathIndex,
		unlockedStopIds,
		onPathStopPress,
		onKeyboardDismiss,
		enabled:
			scopedPanelSpotlightStopId == null && !showThumbMovementControls,
	});

	const blurMapStopFocus = useCallback(() => {
		const root = mapStageRef.current;
		if (!root) {
			return;
		}
		const active = document.activeElement;
		if (active instanceof HTMLElement && root.contains(active)) {
			active.blur();
		}
	}, []);

	// Intentional deps: reset keyboard focus whenever the resolved layout identity changes.
	useEffect(() => {
		setFocusedPathIndex(-1);
	}, [pathFocusOrder.length, resolvedLayoutSeed]);

	useEffect(() => {
		if (scopedPanelSpotlightStopId != null || focusedPathIndex < 0) {
			return;
		}
		const item = pathFocusOrder[focusedPathIndex];
		if (!item?.stopId) {
			return;
		}
		const button = stopButtonRefs.current.get(item.stopId);
		button?.focus();
	}, [focusedPathIndex, pathFocusOrder, scopedPanelSpotlightStopId]);

	const registerHoverCellRoot = useCallback(
		(key: string, node: HTMLElement | null) => {
			if (node) {
				hoverCellRootsRef.current.set(key, node);
				const pointerHover =
					scopedPanelSpotlightStopIdRef.current != null
						? null
						: hoveredHexRef.current;
				syncHexCellHoverFromPointer(key, node, pointerHover, {
					hoverScale: HEXAGON_MAP_HOVER_SCALE,
					activeCharacterKey: activeCharacterKeyForVisualsRef.current,
				});
			} else {
				hoverCellRootsRef.current.delete(key);
			}
		},
		[],
	);

	const resolveHoverDomOptions = useCallback((): HexGridHoverDomOptions => {
		return {
			hoverScale: HEXAGON_MAP_HOVER_SCALE,
			activeCharacterKey: activeCharacterKeyForVisualsRef.current,
			lockedPathCoordKeys: lockedPathCoordKeysRef.current,
		};
	}, []);

	// Intentional deps: re-apply hover DOM when locked segments change (e.g. unlock).
	useEffect(() => {
		if (scopedPanelSpotlightStopIdRef.current != null) {
			return;
		}
		applyHexHoverDomTransition(
			hoveredHexRef.current,
			hoveredHexRef.current,
			hoverCellRootsRef.current,
			resolveHoverDomOptions(),
		);
	}, [lockedPathCoordKeys, resolveHoverDomOptions]);

	const cancelPendingHoverFrame = useCallback(() => {
		if (hoverFrameRef.current != null) {
			cancelAnimationFrame(hoverFrameRef.current);
			hoverFrameRef.current = null;
		}
	}, []);

	const clearPointerHoverState = useCallback(() => {
		cancelPendingHoverFrame();
		const prev = hoveredHexRef.current;
		hoveredHexRef.current = null;
		const keysToClear = new Set(lastPointerHoverAffectedKeysRef.current);
		if (prev) {
			for (const key of collectPointerHoverAffectedCellKeys(prev)) {
				keysToClear.add(key);
			}
		}
		clearHexHoverDomState(
			keysToClear,
			hoverCellRootsRef.current,
			resolveHoverDomOptions(),
		);
		lastPointerHoverAffectedKeysRef.current.clear();
		setHoveredHex(null);
	}, [cancelPendingHoverFrame, resolveHoverDomOptions]);

	const setTransientHoveredCell = useCallback(
		(pos: { row: number; col: number } | null) => {
			if (scopedPanelSpotlightStopIdRef.current != null && pos !== null) {
				return;
			}
			if (pos !== null && isSameHexCoord(hoveredHexRef.current, pos)) {
				return;
			}
			const prev = hoveredHexRef.current;
			hoveredHexRef.current = pos;
			const affectedKeys = new Set([
				...collectPointerHoverAffectedCellKeys(prev),
				...collectPointerHoverAffectedCellKeys(pos),
			]);
			lastPointerHoverAffectedKeysRef.current = affectedKeys;
			applyHexHoverDomTransition(
				prev,
				pos,
				hoverCellRootsRef.current,
				resolveHoverDomOptions(),
			);
			setHoveredHex(pos);
		},
		[resolveHoverDomOptions],
	);

	const stopTypeById = useMemo(() => {
		const map = new Map<
			string,
			"TOPIC" | "BATTLE" | "SCENARIO" | "BOSS" | "ACTION"
		>();
		for (const node of graph.nodes) {
			if (node.kind === "content") {
				map.set(node.stopId, node.stopType);
			}
		}
		return map;
	}, [graph.nodes]);

	const stopCells = useMemo<HexagonStopCellModel[]>(() => {
		const startedAt =
			typeof performance !== "undefined" ? performance.now() : Date.now();
		const cells = displayLayout.pathCells.flatMap((cell) => {
			if (!cell.nodeId) {
				return [];
			}
			const stopId = cell.nodeId.startsWith("sq:map:stop:")
				? cell.nodeId.replace("sq:map:stop:", "")
				: null;
			const iconKey = stopId ?? cell.nodeId;
			const iconSrc = pathStopIcons[iconKey];
			if (!iconSrc) {
				return [];
			}
			const center = hexCellCenter(cell.row, cell.col, metrics);
			const stopType = stopId ? (stopTypeById.get(stopId) ?? null) : null;
			const speechBubbleSrc =
				stopId &&
				stopType &&
				(stopType === "BATTLE" ||
					stopType === "SCENARIO" ||
					stopType === "BOSS")
					? (pathStopSpeechBubbles[stopId] ?? null)
					: null;
			return [
				{
					nodeId: cell.nodeId,
					row: cell.row,
					col: cell.col,
					pathIndex: cell.pathIndex,
					left: center.left,
					top: center.top,
					stopId,
					stopType,
					iconKey,
					iconSrc,
					speechBubbleSrc,
					isUnlocked: stopId ? unlockedStopIds.has(stopId) : false,
					isCompleted: stopId ? completedStopIds.has(stopId) : false,
					focusOrderIndex: stopId
						? (focusOrderIndexByStopId.get(stopId) ?? -1)
						: -1,
					tooltipPlacement: radixTooltipFromPlacement(
						getTooltipPlacement(
							cell.row,
							cell.col,
							displayRows,
							displayCols,
						),
					),
				},
			];
		});
		const elapsedMs =
			(typeof performance !== "undefined"
				? performance.now()
				: Date.now()) - startedAt;
		if (elapsedMs >= 8) {
			logHexagonMapPerfEvent("stop cells computed", {
				stopCellCount: cells.length,
				rows: displayRows,
				displayCols,
				elapsedMs: Math.round(elapsedMs),
			});
		}
		return cells;
	}, [
		completedStopIds,
		displayCols,
		displayLayout.pathCells,
		focusOrderIndexByStopId,
		metrics,
		pathStopIcons,
		pathStopSpeechBubbles,
		stopTypeById,
		displayRows,
		unlockedStopIds,
	]);

	const playerCell = useMemo(
		() => resolvePlayerPathCell(playerStopId, graph, displayLayout),
		[displayLayout, graph, playerStopId],
	);
	const effectivePlayerPathPosition = showAuthoringControls
		? layoutScopedPlayerPathPosition
		: playerPathPosition;
	const playerAnchorCoord = useMemo(
		() =>
			resolvePlayerAnchorCoord({
				playerPathPosition: effectivePlayerPathPosition,
				playerStopId,
				graph,
				layout: displayLayout,
				pathProgressByCoordKey,
				preferStartAnchorWhenNoPathPosition: showAuthoringControls,
			}),
		[
			displayLayout,
			effectivePlayerPathPosition,
			graph,
			pathProgressByCoordKey,
			playerStopId,
			showAuthoringControls,
		],
	);
	const playerNextAnchorCoord = useMemo(() => {
		if (!playerAnchorCoord) {
			return null;
		}
		const nextCoord = resolveNextPathCoord(
			playerAnchorCoord,
			displayLayout.pathCoordSet,
			pathProgressByCoordKey,
		);
		return nextCoord ?? null;
	}, [displayLayout.pathCoordSet, pathProgressByCoordKey, playerAnchorCoord]);
	const displayFrameKey = `${displayRows}:${displayCols}:${Math.round(metrics.hexagonWidth)}:${Math.round(metrics.hexagonHeight)}`;
	const playerAnchorSyncKey = useMemo(() => {
		const pathPositionKey = effectivePlayerPathPosition
			? `${effectivePlayerPathPosition.pathProgress}:${effectivePlayerPathPosition.branchCorridorId ?? ""}:${effectivePlayerPathPosition.pathNodeId ?? ""}`
			: "none";
		const coordKeyPart = playerAnchorCoord
			? coordKey(playerAnchorCoord.row, playerAnchorCoord.col)
			: "none";
		return `${logicalLayoutIdentity}:${displayFrameKey}:${pathPositionKey}:${coordKeyPart}`;
	}, [
		displayFrameKey,
		effectivePlayerPathPosition,
		logicalLayoutIdentity,
		playerAnchorCoord,
	]);
	const playerAnchorSyncKeyRef = useRef(playerAnchorSyncKey);
	playerAnchorSyncKeyRef.current = playerAnchorSyncKey;
	const [previewMovementAnchor, setPreviewMovementAnchor] = useState<{
		coord: { row: number; col: number };
		syncKey: string;
	} | null>(null);
	const pendingScopedPreviewWalkRef = useRef<HexCoord[] | null>(null);
	const previousSpotlightStopForFocusRef = useRef<string | null>(null);
	const lastCompletedPreviewNavigationTokenRef = useRef(-1);
	const pendingPreviewNavigationTokenRef = useRef<number | null>(null);
	const commitPlayerPathPositionForCoord = useCallback(
		(coord: { row: number; col: number }) => {
			const pathPosition = resolveMapPlayerPathPositionForPathCell(
				coord,
				pathProgressByCoordKey,
				branchCorridorIdByCoordKey,
				pathCellByCoord.get(coordKey(coord.row, coord.col)),
			);
			if (pathPosition == null) {
				return;
			}
			const normalized = normalizeMapPlayerPathPosition(pathPosition);
			if (showAuthoringControls) {
				setLayoutScopedPlayerPathPosition(normalized);
			}
			onPlayerPathPositionChange?.(normalized);
		},
		[
			branchCorridorIdByCoordKey,
			onPlayerPathPositionChange,
			pathCellByCoord,
			pathProgressByCoordKey,
			showAuthoringControls,
		],
	);
	const movementAnchorCoord =
		scopedPanelSpotlightStopId != null && previewMovementAnchor
			? previewMovementAnchor.coord
			: playerAnchorCoord;
	const movementAnchorSyncKey =
		scopedPanelSpotlightStopId != null && previewMovementAnchor
			? previewMovementAnchor.syncKey
			: playerAnchorSyncKey;
	const characterMovement = useHexagonMapCharacterMovement({
		anchorCoord: movementAnchorCoord,
		anchorSyncKey: movementAnchorSyncKey,
		nextAnchorCoord: playerNextAnchorCoord,
		metrics,
		playerIcons,
		msPerTile: characterMovementMsPerTile,
		reducedMotion: reducedMotion ?? false,
		onMovementComplete: (coord) => {
			if (scopedPanelSpotlightStopIdRef.current == null) {
				return;
			}
			setPreviewMovementAnchor(null);
			commitPlayerPathPositionForCoord(coord);
		},
	});
	const activePlayerCoord = useMemo(() => {
		const candidate =
			characterMovement.resolvePathOriginCoord() ??
			characterMovement.playerCoord ??
			playerAnchorCoord;
		if (!candidate) {
			return playerCell
				? { row: playerCell.row, col: playerCell.col }
				: null;
		}
		const key = coordKey(candidate.row, candidate.col);
		if (displayLayout.pathCoordSet.has(key)) {
			return candidate;
		}
		const snapped = resolveNearestPathTileFromDisplayCenter(
			hexCellCenter(candidate.row, candidate.col, metrics),
			displayLayout.pathCoordSet,
			metrics,
		);
		if (snapped) {
			return snapped;
		}
		if (playerCell) {
			return { row: playerCell.row, col: playerCell.col };
		}
		return null;
	}, [
		characterMovement.playerCoord,
		displayLayout.pathCoordSet,
		metrics,
		playerAnchorCoord,
		playerCell,
	]);
	activePlayerCoordRef.current = activePlayerCoord;
	const playerAnchorCoordRef = useRef(playerAnchorCoord);
	playerAnchorCoordRef.current = playerAnchorCoord;

	const spotlightPathCoordKeysRef = useRef<Set<string>>(
		EMPTY_SPOTLIGHT_COORD_KEYS,
	);
	const clearSpotlightPath = useCallback(() => {
		spotlightPathCoordKeysRef.current = EMPTY_SPOTLIGHT_COORD_KEYS;
	}, []);

	const spotlightRouteDuringScopedPanel = useCallback(
		(route: HexCoord[] | null | undefined) => {
			if (
				scopedPanelSpotlightStopIdRef.current == null ||
				!route?.length
			) {
				return;
			}
			const nextKeys = new Set(
				route.map((step) => coordKey(step.row, step.col)),
			);
			const previousKeys = spotlightPathCoordKeysRef.current;
			const unchanged =
				nextKeys.size === previousKeys.size &&
				[...nextKeys].every((key) => previousKeys.has(key));
			if (unchanged) {
				return;
			}
			spotlightPathCoordKeysRef.current = nextKeys;
		},
		[],
	);

	const syncScopedPanelSpotlightRouteForStop = useCallback(
		(stopId: string) => {
			if (scopedPanelSpotlightStopIdRef.current == null) {
				return;
			}
			const targetCell = resolvePlayerPathCell(
				stopId,
				graph,
				displayLayout,
			);
			const originCoord = ensureOnPathCoord(
				characterMovement.resolvePathOriginCoord() ?? activePlayerCoord,
				displayLayout.pathCoordSet,
				metrics,
				playerAnchorCoord,
			);
			if (!targetCell || !originCoord) {
				return;
			}
			const route = findPathTileRoute(
				originCoord,
				{ row: targetCell.row, col: targetCell.col },
				displayLayout.pathCoordSet,
			);
			spotlightRouteDuringScopedPanel(route);
		},
		[
			activePlayerCoord,
			characterMovement,
			displayLayout,
			graph,
			metrics,
			playerAnchorCoord,
			spotlightRouteDuringScopedPanel,
		],
	);

	const scopedPanelNavAvailability = useMemo(() => {
		if (!scopedPanelSpotlightStopId) {
			return null;
		}
		const originCoord = ensureOnPathCoord(
			characterMovement.resolvePathOriginCoord() ?? activePlayerCoord,
			displayLayout.pathCoordSet,
			metrics,
			playerAnchorCoord,
		);
		return resolveScopedPanelNavAvailability({
			anchorStopId: scopedPanelSpotlightStopId,
			pathFocusOrder,
			originCoord,
			graph,
			layout: displayLayout,
			pathCoordSet: displayLayout.pathCoordSet,
			pathProgressByCoordKey,
			pathIndexByCoordKey,
			furthestUnlockedPathProgress,
			isPathIndexVisible: isPathIndexVisibleForMovement,
			unlockedStopIds,
			resolveTargetCell: resolvePlayerPathCell,
		});
	}, [
		activePlayerCoord,
		characterMovement.isMoving,
		characterMovement.playerCoord,
		displayLayout,
		furthestUnlockedPathProgress,
		graph,
		isPathIndexVisibleForMovement,
		metrics,
		pathFocusOrder,
		pathIndexByCoordKey,
		pathProgressByCoordKey,
		playerAnchorCoord,
		scopedPanelSpotlightStopId,
		unlockedStopIds,
	]);

	const scopedPanelNavAvailabilityKey = scopedPanelNavAvailability
		? `${scopedPanelNavAvailability.prevStopId ?? ""}:${scopedPanelNavAvailability.nextStopId ?? ""}:${scopedPanelNavAvailability.canGoPrev}:${scopedPanelNavAvailability.canGoNext}`
		: null;
	const previousScopedPanelNavAvailabilityKeyRef = useRef<string | null>(
		null,
	);

	useEffect(() => {
		if (
			previousScopedPanelNavAvailabilityKeyRef.current ===
			scopedPanelNavAvailabilityKey
		) {
			return;
		}
		previousScopedPanelNavAvailabilityKeyRef.current =
			scopedPanelNavAvailabilityKey;
		onScopedPanelNavAvailabilityChange?.(scopedPanelNavAvailability);
	}, [
		onScopedPanelNavAvailabilityChange,
		scopedPanelNavAvailability,
		scopedPanelNavAvailabilityKey,
	]);

	useEffect(() => {
		if (scopedPanelSpotlightStopId == null) {
			clearSpotlightPath();
			// Next preview open must treat first insets as one-shot, not "refine".
			previousScopedPanelSheetInsetsKeyRef.current = null;
		}
	}, [clearSpotlightPath, scopedPanelSpotlightStopId]);

	useEffect(() => {
		return () => {
			clearSpotlightPath();
		};
	}, [clearSpotlightPath]);

	const resolveOnPathOriginForSpotlight = useCallback((): HexCoord | null => {
		return ensureOnPathCoord(
			characterMovement.resolvePathOriginCoord(),
			displayLayout.pathCoordSet,
			metrics,
			activePlayerCoord ?? playerAnchorCoord,
		);
	}, [
		activePlayerCoord,
		characterMovement,
		displayLayout.pathCoordSet,
		metrics,
		playerAnchorCoord,
	]);

	const {
		activeRouteCoordKeys: scopedPanelSpotlightActiveRouteCoordKeys,
		renderCoordKeys: scopedPanelSpotlightRenderCoordKeys,
		exitingTiles: scopedPanelSpotlightExitingTiles,
	} = useScopedPanelSpotlightPathKeys({
		spotlightStopId: scopedPanelSpotlightStopId,
		activePlayerCoord,
		resolvePathOriginCoord: resolveOnPathOriginForSpotlight,
		graph,
		displayLayout,
	});

	useEffect(() => {
		spotlightPathCoordKeysRef.current = new Set(
			scopedPanelSpotlightRenderCoordKeys,
		);
	}, [scopedPanelSpotlightRenderCoordKeys]);

	const prevScopedPanelSpotlightStopIdRef = useRef<string | null>(null);
	useEffect(() => {
		const stopId = scopedPanelSpotlightStopId;
		const previousStopId = prevScopedPanelSpotlightStopIdRef.current;
		prevScopedPanelSpotlightStopIdRef.current = stopId;

		if (stopId == null) {
			pendingScopedPreviewWalkRef.current = null;
			setPreviewMovementAnchor(null);
			previousSpotlightStopForFocusRef.current = null;
			lastCompletedPreviewNavigationTokenRef.current = -1;
			pendingPreviewNavigationTokenRef.current = null;
			return;
		}

		if (previousStopId != null && previousStopId !== stopId) {
			pendingScopedPreviewWalkRef.current = null;
			setPreviewMovementAnchor(null);
			pendingPreviewNavigationTokenRef.current = null;
		}
	}, [scopedPanelSpotlightStopId]);

	useLayoutEffect(() => {
		const route = pendingScopedPreviewWalkRef.current;
		if (!route?.length || previewMovementAnchor == null) {
			return;
		}
		pendingScopedPreviewWalkRef.current = null;
		const started = characterMovement.startMovement(route);
		if (started && pendingPreviewNavigationTokenRef.current != null) {
			lastCompletedPreviewNavigationTokenRef.current =
				pendingPreviewNavigationTokenRef.current;
			pendingPreviewNavigationTokenRef.current = null;
		}
	}, [characterMovement, previewMovementAnchor]);

	const restingDisplayCenter = useMemo(() => {
		if (!activePlayerCoord) {
			return null;
		}
		return hexCellCenter(
			activePlayerCoord.row,
			activePlayerCoord.col,
			metrics,
		);
	}, [activePlayerCoord, metrics]);
	const characterRestingCenterForSprite =
		layoutRestoreVisualCenter ?? restingDisplayCenter;

	useEffect(() => {
		if (
			characterLayoutMotionPhase === "stable" &&
			restingDisplayCenter != null
		) {
			lastStablePlayerDisplayCenterRef.current = restingDisplayCenter;
		}
	}, [characterLayoutMotionPhase, restingDisplayCenter]);

	/**
	 * Seed / settings path refresh: restore player onto nearest stop (else nearest
	 * path tile / start) with the same scale-fade out→in cadence as map tiles.
	 */
	// Intentional deps: restore only on layout identity change
	useEffect(() => {
		const previousIdentity = lastLayoutIdentityForRestoreRef.current;
		if (previousIdentity === logicalLayoutIdentity) {
			return;
		}
		lastLayoutIdentityForRestoreRef.current = logicalLayoutIdentity;

		const restored = resolveRestoredPlayerPathCoordAfterLayoutChange({
			displayCenter: lastStablePlayerDisplayCenterRef.current,
			layout: displayLayout,
			metrics,
		});

		const clearMotionTimers = () => {
			if (characterLayoutMotionTimerRef.current != null) {
				clearTimeout(characterLayoutMotionTimerRef.current);
				characterLayoutMotionTimerRef.current = null;
			}
		};
		clearMotionTimers();
		characterMovement.cancelMovement();

		if (!restored) {
			if (showAuthoringControls) {
				setLayoutScopedPlayerPathPosition(null);
			}
			setCharacterLayoutMotionPhase("stable");
			setLayoutRestoreVisualCenter(null);
			return;
		}

		const applyRestore = () => {
			commitPlayerPathPositionForCoord(restored);
			characterMovement.snapToCoord(restored);
		};

		if (reducedMotion) {
			applyRestore();
			setCharacterLayoutMotionPhase("stable");
			setLayoutRestoreVisualCenter(null);
			return;
		}

		const holdCenter =
			lastStablePlayerDisplayCenterRef.current ??
			hexCellCenter(restored.row, restored.col, metrics);
		setLayoutRestoreVisualCenter(holdCenter);
		setCharacterLayoutMotionPhase("exit");

		characterLayoutMotionTimerRef.current = setTimeout(() => {
			applyRestore();
			setLayoutRestoreVisualCenter(
				hexCellCenter(restored.row, restored.col, metrics),
			);
			setCharacterLayoutMotionPhase("enter");
			characterLayoutMotionTimerRef.current = setTimeout(() => {
				setCharacterLayoutMotionPhase("stable");
				setLayoutRestoreVisualCenter(null);
				characterLayoutMotionTimerRef.current = null;
			}, gridAnimationDurationMs);
		}, gridAnimationDurationMs);

		return () => {
			clearMotionTimers();
		};
	}, [logicalLayoutIdentity]);

	const activePlayerPathIndex = useMemo(() => {
		const resolveIndexForCoord = (coord: { row: number; col: number }) => {
			const mapped = pathIndexByCoordKey.get(
				coordKey(coord.row, coord.col),
			);
			if (mapped != null && mapped >= 0) {
				return mapped;
			}
			for (const cell of displayLayout.pathCells) {
				if (cell.row === coord.row && cell.col === coord.col) {
					return cell.pathIndex;
				}
			}
			return -1;
		};

		if (activePlayerCoord) {
			const fromCoord = resolveIndexForCoord(activePlayerCoord);
			if (fromCoord >= 0) {
				return fromCoord;
			}
		}
		if (playerCell) {
			return playerCell.pathIndex;
		}
		return resolvePlayerPathIndex(playerStopId, graph, displayLayout);
	}, [
		activePlayerCoord,
		displayLayout.pathCells,
		graph,
		pathIndexByCoordKey,
		playerCell,
		playerStopId,
	]);
	const isCharacterPathVisualActive = useMemo(() => {
		if (!activePlayerCoord || activePlayerPathIndex < 0) {
			return false;
		}
		if (!initialAnimationEnabled) {
			return true;
		}
		return (
			isPathRevealComplete || isPathIndexVisible(activePlayerPathIndex)
		);
	}, [
		activePlayerCoord,
		activePlayerPathIndex,
		initialAnimationEnabled,
		isPathIndexVisible,
		isPathRevealComplete,
	]);
	const isCharacterPathVisualActiveForMap =
		scopedPanelSpotlightStopId != null
			? activePlayerCoord != null && activePlayerPathIndex >= 0
			: isCharacterPathVisualActive;
	const characterPathVisualActiveCoord = isCharacterPathVisualActiveForMap
		? activePlayerCoord
		: null;
	activeCharacterKeyForVisualsRef.current =
		characterPathVisualActiveCoord != null
			? coordKey(
					characterPathVisualActiveCoord.row,
					characterPathVisualActiveCoord.col,
				)
			: null;

	const prevActiveCharacterVisualKeyRef = useRef<string | null>(null);
	useEffect(() => {
		const prev = prevActivePlayerCoordRef.current;
		const next = activePlayerCoord;
		const prevVisualKey = prevActiveCharacterVisualKeyRef.current;
		const nextVisualKey = activeCharacterKeyForVisualsRef.current;
		const coordChanged = prev?.row !== next?.row || prev?.col !== next?.col;
		const visualKeyChanged = prevVisualKey !== nextVisualKey;
		if (!coordChanged && !visualKeyChanged) {
			return;
		}
		const effectivePointerHover =
			scopedPanelSpotlightStopId != null ? null : hoveredHexRef.current;
		applyHexCharacterActiveDomTransition(
			prev,
			next,
			effectivePointerHover,
			hoverCellRootsRef.current,
			{
				hoverScale: HEXAGON_MAP_HOVER_SCALE,
				activeCharacterKey: nextVisualKey,
			},
		);
		prevActivePlayerCoordRef.current = next;
		prevActiveCharacterVisualKeyRef.current = nextVisualKey;
	}, [
		activePlayerCoord,
		isCharacterPathVisualActive,
		scopedPanelSpotlightStopId,
	]);

	const gridCellCount = gridCells.length;
	const stopCellCount = stopCells.length;

	const handlePathTilePress = useCallback(
		(target: { row: number; col: number }) => {
			if (scopedPanelSpotlightStopIdRef.current != null) {
				pendingScopedPreviewWalkRef.current = null;
				setPreviewMovementAnchor(null);
			}
			const originCoord =
				characterMovement.resolvePathOriginCoord() ?? activePlayerCoord;
			if (!originCoord) {
				return;
			}
			const targetKey = coordKey(target.row, target.col);
			if (!displayLayout.pathCoordSet.has(targetKey)) {
				return;
			}
			if (
				originCoord.row === target.row &&
				originCoord.col === target.col
			) {
				return;
			}
			const targetPathIndex = pathIndexByCoordKey.get(targetKey);
			const targetPathProgress = pathProgressByCoordKey.get(targetKey);
			if (targetPathProgress == null) {
				return;
			}
			if (
				!isPathTileTraversalEnabled({
					row: target.row,
					col: target.col,
					pathProgress: targetPathProgress,
					pathCoordSet: displayLayout.pathCoordSet,
					furthestUnlockedPathProgress,
					isPathIndexVisible: isPathIndexVisibleForMovement,
					pathIndexForVisibility: targetPathIndex,
				})
			) {
				return;
			}
			const route = findPathTileRoute(
				originCoord,
				target,
				displayLayout.pathCoordSet,
			);
			if (route?.length) {
				spotlightRouteDuringScopedPanel(route);
			}
			if (!route || route.length < 2) {
				return;
			}
			if (
				!isPathRouteTraversalEnabled({
					route,
					pathCoordSet: displayLayout.pathCoordSet,
					pathProgressByCoordKey,
					pathIndexByCoordKey,
					furthestUnlockedPathProgress,
					isPathIndexVisible: isPathIndexVisibleForMovement,
				})
			) {
				return;
			}
			const pathCell = pathCellByCoord.get(targetKey);
			const stopIdAtTarget =
				pathCell?.nodeId?.startsWith("sq:map:stop:") === true
					? pathCell.nodeId.replace("sq:map:stop:", "")
					: null;
			if (stopIdAtTarget) {
				onMapPathSelectionChange?.(stopIdAtTarget);
				return;
			}
			commitPlayerPathPositionForCoord(target);
			characterMovement.startMovement(route);
			onMapPathSelectionChange?.(null);
		},
		[
			activePlayerCoord,
			characterMovement,
			commitPlayerPathPositionForCoord,
			displayLayout.pathCoordSet,
			furthestUnlockedPathProgress,
			isPathIndexVisibleForMovement,
			onMapPathSelectionChange,
			pathCellByCoord,
			pathIndexByCoordKey,
			pathProgressByCoordKey,
			spotlightRouteDuringScopedPanel,
		],
	);

	const navigateCharacterToStop = useCallback(
		(stopId: string, options?: { notifyPress?: boolean }) => {
			if (!unlockedStopIds.has(stopId)) {
				return;
			}
			if (!characterWalksToStops) {
				if (options?.notifyPress !== false) {
					onPathStopPress?.(stopId);
				}
				return;
			}
			const targetCell = resolvePlayerPathCell(
				stopId,
				graph,
				displayLayout,
			);
			const rawOrigin =
				characterMovement.resolvePathOriginCoord() ?? activePlayerCoord;
			const originCoord = ensureOnPathCoord(
				rawOrigin,
				displayLayout.pathCoordSet,
				metrics,
				playerAnchorCoord,
			);
			if (targetCell && originCoord) {
				// Recover sprite before pathfinding when hold/display coords went off-path
				// (e.g. display-frame shift while preview sheets resize the map).
				if (
					rawOrigin != null &&
					isOffPathCoord(rawOrigin, displayLayout.pathCoordSet)
				) {
					characterMovement.snapToCoord(originCoord);
				}
				const route = findPathTileRoute(
					originCoord,
					{ row: targetCell.row, col: targetCell.col },
					displayLayout.pathCoordSet,
				);
				const isScopedPanelPreview =
					scopedPanelSpotlightStopIdRef.current != null;
				if (route && route.length >= 2) {
					if (isScopedPanelPreview) {
						pendingScopedPreviewWalkRef.current = route;
						setPreviewMovementAnchor({
							coord: originCoord,
							syncKey: playerAnchorSyncKeyRef.current,
						});
					} else {
						commitPlayerPathPositionForCoord(targetCell);
						characterMovement.startMovement(route);
					}
				} else {
					// No walkable route: teleport sprite onto the stop (not path-position-only).
					characterMovement.snapToCoord({
						row: targetCell.row,
						col: targetCell.col,
					});
					commitPlayerPathPositionForCoord(targetCell);
					if (isScopedPanelPreview) {
						setPreviewMovementAnchor(null);
					}
				}
				syncScopedPanelSpotlightRouteForStop(stopId);
			}
			if (options?.notifyPress !== false) {
				onPathStopPress?.(stopId);
			}
		},
		[
			characterWalksToStops,
			activePlayerCoord,
			characterMovement,
			commitPlayerPathPositionForCoord,
			displayLayout,
			graph,
			metrics,
			onPathStopPress,
			playerAnchorCoord,
			syncScopedPanelSpotlightRouteForStop,
			unlockedStopIds,
		],
	);

	const handlePathStopClick = useCallback(
		(stopId: string) => {
			if (!unlockedStopIds.has(stopId)) {
				return;
			}
			onPathStopPress?.(stopId);
		},
		[onPathStopPress, unlockedStopIds],
	);

	useEffect(() => {
		if (!requestedNavigateStopId) {
			return;
		}
		// Stop preview uses spotlight pan/zoom — walking the avatar remounts path tiles.
		if (scopedPanelSpotlightStopIdRef.current != null) {
			onRequestedNavigateStopHandled?.();
			return;
		}
		navigateCharacterToStop(requestedNavigateStopId, {
			notifyPress: false,
		});
		onRequestedNavigateStopHandled?.();
	}, [
		navigateCharacterToStop,
		onRequestedNavigateStopHandled,
		requestedNavigateStopId,
	]);

	const setContainerRef = useCallback((node: HTMLDivElement | null) => {
		mapStageRef.current = node;
	}, []);
	const setContentStageRef = useCallback(
		(node: HTMLDivElement | null) => {
			contentStageRef.current = node;
			setPanZoomContentRef(node);
		},
		[setPanZoomContentRef],
	);

	const resolvePathTilePressFromPointer = useCallback(
		(clientX: number, clientY: number) => {
			const stage = contentStageRef.current;
			if (!stage) {
				return;
			}
			const target = resolveHoveredHexFromPointer({
				pointer: { clientX, clientY },
				stageRect: stage.getBoundingClientRect(),
				scale,
				metrics,
				rows: displayRows,
				cols: displayCols,
			});
			if (target) {
				handlePathTilePress(target);
			}
		},
		[displayCols, displayRows, handlePathTilePress, metrics, scale],
	);

	const handleViewportPointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (scopedPanelSpotlightStopId != null) {
				return;
			}
			pathClickStartRef.current = {
				clientX: event.clientX,
				clientY: event.clientY,
			};
		},
		[scopedPanelSpotlightStopId],
	);

	const handleContentPointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (scopedPanelSpotlightStopId != null) {
				return;
			}
			pathClickStartRef.current = {
				clientX: event.clientX,
				clientY: event.clientY,
			};
		},
		[scopedPanelSpotlightStopId],
	);

	const handleContentPointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (scopedPanelSpotlightStopId != null) {
				return;
			}
			const stage = contentStageRef.current;
			if (!stage) {
				return;
			}
			const pointer = {
				clientX: event.clientX,
				clientY: event.clientY,
			};
			cancelPendingHoverFrame();
			hoverFrameRef.current = requestAnimationFrame(() => {
				hoverFrameRef.current = null;
				if (scopedPanelSpotlightStopIdRef.current != null) {
					return;
				}
				const next = resolveHoveredHexFromPointer({
					pointer,
					stageRect: stage.getBoundingClientRect(),
					scale,
					metrics,
					rows: displayRows,
					cols: displayCols,
				});
				setTransientHoveredCell(next);
			});
		},
		[
			cancelPendingHoverFrame,
			displayCols,
			displayRows,
			metrics,
			scale,
			scopedPanelSpotlightStopId,
			setTransientHoveredCell,
		],
	);

	const handleViewportPointerUp = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (scopedPanelSpotlightStopId != null) {
				return;
			}
			const clickStart = pathClickStartRef.current;
			pathClickStartRef.current = null;
			if (!clickStart) {
				return;
			}
			const moveDistance = Math.hypot(
				event.clientX - clickStart.clientX,
				event.clientY - clickStart.clientY,
			);
			if (moveDistance <= PATH_TILE_CLICK_MAX_MOVE_PX) {
				resolvePathTilePressFromPointer(event.clientX, event.clientY);
			}
		},
		[resolvePathTilePressFromPointer, scopedPanelSpotlightStopId],
	);

	const handleContentPointerUp = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (scopedPanelSpotlightStopId != null) {
				return;
			}
			const clickStart = pathClickStartRef.current;
			pathClickStartRef.current = null;
			if (clickStart) {
				const moveDistance = Math.hypot(
					event.clientX - clickStart.clientX,
					event.clientY - clickStart.clientY,
				);
				if (moveDistance <= PATH_TILE_CLICK_MAX_MOVE_PX) {
					resolvePathTilePressFromPointer(
						event.clientX,
						event.clientY,
					);
				}
			}
		},
		[resolvePathTilePressFromPointer, scopedPanelSpotlightStopId],
	);

	const handleViewportPointerLeave = useCallback(() => {
		pathClickStartRef.current = null;
	}, []);

	const handleContentPointerLeave = useCallback(() => {
		if (scopedPanelSpotlightStopId != null) {
			return;
		}
		pathClickStartRef.current = null;
		clearPointerHoverState();
	}, [clearPointerHoverState, scopedPanelSpotlightStopId]);

	useLayoutEffect(() => {
		if (scopedPanelSpotlightStopId == null) {
			return;
		}
		setFocusedPathIndex(-1);
		clearPointerHoverState();
		blurMapStopFocus();
		return () => {
			setFocusedPathIndex(-1);
			clearPointerHoverState();
			blurMapStopFocus();
		};
	}, [blurMapStopFocus, clearPointerHoverState, scopedPanelSpotlightStopId]);

	useEffect(() => {
		return () => {
			cancelPendingHoverFrame();
		};
	}, [cancelPendingHoverFrame]);

	const renderSummaryRef = useRef<{
		resolvedLayoutSeed: string;
		rows: number;
		displayCols: number;
		gridCellCount: number;
		stopCellCount: number;
		environmentCount: number;
		containerWidth: number;
		containerHeight: number;
		scale: number;
		userZoom: number;
		visibleRowCount: number;
		revealedThroughIndex: number;
		playerPathIndex: number;
		highlightedStopId: string | null;
	} | null>(null);

	useEffect(() => {
		const nextSummary = {
			resolvedLayoutSeed,
			rows: displayRows,
			displayCols,
			gridCellCount,
			stopCellCount,
			environmentCount: environmentPlacementModels.length,
			containerWidth: containerSize.width,
			containerHeight: containerSize.height,
			scale,
			userZoom,
			visibleRowCount,
			revealedThroughIndex,
			playerPathIndex: activePlayerPathIndex,
			highlightedStopId: highlightedStopId ?? null,
		};
		const prevSummary = renderSummaryRef.current;
		renderSummaryRef.current = nextSummary;
		if (!prevSummary) {
			logHexagonMapPerfEvent("render summary", nextSummary);
			return;
		}
		const changedKeys: string[] = [];
		for (const [key, value] of Object.entries(nextSummary)) {
			if (prevSummary[key as keyof typeof prevSummary] !== value) {
				changedKeys.push(key);
			}
		}
		if (changedKeys.length > 0) {
			logHexagonMapPerfEvent("render inputs changed", {
				changedKeys,
				...nextSummary,
			});
		}
	}, [
		containerSize.height,
		containerSize.width,
		displayCols,
		environmentPlacementModels.length,
		gridCellCount,
		activePlayerPathIndex,
		highlightedStopId,
		resolvedLayoutSeed,
		revealedThroughIndex,
		displayRows,
		scale,
		stopCellCount,
		userZoom,
		visibleRowCount,
	]);

	useEffect(() => {
		if (reducedMotion) {
			return;
		}
		logHexagonMapPerfEvent("animation progress", {
			resolvedLayoutSeed,
			rows: displayRows,
			pathLength,
			visibleRowCount,
			revealedThroughIndex,
		});
	}, [
		pathLength,
		reducedMotion,
		resolvedLayoutSeed,
		revealedThroughIndex,
		displayRows,
		visibleRowCount,
	]);

	const stopOverlaySize = resolveHexagonStopOverlaySizePx(
		metrics.hexagonWidth,
	);
	const characterOverlaySize = resolveHexagonCharacterOverlaySizePx(
		metrics.hexagonWidth,
		metrics.hexagonHeight,
	);
	const characterIsOnMap =
		containerSize.width > 0 &&
		containerSize.height > 0 &&
		activePlayerPathIndex >= 0 &&
		(isCharacterPathVisualActiveForMap ||
			isPathRevealComplete ||
			isPathIndexVisible(activePlayerPathIndex));

	const isScopedPanelOpen = scopedPanelSpotlightStopId != null;
	const mapDimOverlay = useScopedPanelOverlayVisibility(isScopedPanelOpen, {
		disabled: reducedMotion ?? false,
	});

	const handlePanToCharacter = useCallback(
		(contentCenter: { left: number; top: number }) => {
			panToCenterContentPoint({
				x: contentCenter.left,
				y: contentCenter.top,
			});
		},
		[panToCenterContentPoint],
	);

	const stopIdAtActivePlayerCoord = useMemo(() => {
		if (!activePlayerCoord) {
			return null;
		}
		const cell = pathCellByCoord.get(
			coordKey(activePlayerCoord.row, activePlayerCoord.col),
		);
		if (!cell?.nodeId?.startsWith("sq:map:stop:")) {
			return null;
		}
		return cell.nodeId.replace("sq:map:stop:", "");
	}, [activePlayerCoord, pathCellByCoord]);

	const isPlayerOnFurthestUnlockedStop = useMemo(() => {
		if (!furthestUnlockedStopOnPath || !activePlayerCoord) {
			return false;
		}
		if (
			activePlayerCoord.row === furthestUnlockedStopOnPath.row &&
			activePlayerCoord.col === furthestUnlockedStopOnPath.col
		) {
			return true;
		}
		return stopIdAtActivePlayerCoord === furthestUnlockedStopOnPath.stopId;
	}, [
		activePlayerCoord,
		furthestUnlockedStopOnPath,
		stopIdAtActivePlayerCoord,
	]);

	const panZoomViewportNodeRef = useRef<HTMLDivElement | null>(null);
	const handlePanZoomViewportRef = useCallback(
		(node: HTMLDivElement | null) => {
			panZoomViewportNodeRef.current = node;
			setPanZoomViewportRef(node);
			onMapViewportElement?.(node);
		},
		[onMapViewportElement, setPanZoomViewportRef],
	);

	const userZoomRef = useRef(userZoom);
	userZoomRef.current = userZoom;
	const preScopedPanelZoomRef = useRef<number | null>(null);
	const previousScopedPanelStopIdRef = useRef<string | null>(null);
	const rememberPreScopedPanelZoom = useCallback(() => {
		if (preScopedPanelZoomRef.current == null) {
			preScopedPanelZoomRef.current = userZoomRef.current;
		}
	}, []);

	const focusViewportOnSpotlightStop = useCallback(
		(stopId: string, options?: { animate?: boolean }) => {
			rememberPreScopedPanelZoom();
			const targetCell = resolvePlayerPathCell(
				stopId,
				graph,
				displayLayout,
			);
			if (!targetCell) {
				setUserZoom(HEXAGON_MAP_MAX_USER_ZOOM, { rapid: false });
				return;
			}
			const center = hexCellCenter(
				targetCell.row,
				targetCell.col,
				metrics,
			);
			const targetScale = fitScale * HEXAGON_MAP_MAX_USER_ZOOM;
			const contentPoint = resolveMapStopPreviewSpotlightContentPoint({
				cellCenterX: center.left,
				cellCenterY: center.top,
				hexagonHeight: metrics.hexagonHeight,
				userZoom: HEXAGON_MAP_MAX_USER_ZOOM,
				contentMapScale: targetScale,
			});
			const insets =
				scopedPanelSheetInsetsRef.current ??
				lastKnownScopedPanelSheetInsetsRef.current;
			const viewportHeight = panZoomViewportSize.height;
			const viewportCenterOffset =
				insets != null && viewportHeight > 0
					? {
							y: resolveScopedPanelMapBandViewportCenterOffsetY({
								topInsetPx: insets.topPx,
								bottomInsetPx: insets.bottomPx,
								viewportHeightPx: viewportHeight,
							}),
						}
					: undefined;
			panAndZoomToCenterContentPoint(
				contentPoint,
				HEXAGON_MAP_MAX_USER_ZOOM,
				{
					animate: options?.animate ?? true,
					viewportCenterOffset,
				},
			);
		},
		[
			displayLayout,
			fitScale,
			graph,
			metrics,
			panAndZoomToCenterContentPoint,
			panZoomViewportSize.height,
			rememberPreScopedPanelZoom,
			setUserZoom,
		],
	);

	const syncScopedPanelSpotlightRouteForStopRef = useRef(
		syncScopedPanelSpotlightRouteForStop,
	);
	syncScopedPanelSpotlightRouteForStopRef.current =
		syncScopedPanelSpotlightRouteForStop;
	const focusViewportOnSpotlightStopRef = useRef(
		focusViewportOnSpotlightStop,
	);
	focusViewportOnSpotlightStopRef.current = focusViewportOnSpotlightStop;
	const navigateCharacterToStopRef = useRef(navigateCharacterToStop);
	navigateCharacterToStopRef.current = navigateCharacterToStop;
	const characterMovementRef = useRef(characterMovement);
	characterMovementRef.current = characterMovement;

	useEffect(() => {
		const previousStopId = previousScopedPanelStopIdRef.current;
		const nextStopId = scopedPanelSpotlightStopId;

		if (nextStopId != null && previousStopId == null) {
			rememberPreScopedPanelZoom();
		}

		if (nextStopId == null && previousStopId != null) {
			const savedZoom = preScopedPanelZoomRef.current;
			preScopedPanelZoomRef.current = null;
			if (savedZoom != null) {
				setUserZoom(savedZoom, { rapid: false });
			}
		}

		previousScopedPanelStopIdRef.current = nextStopId;
	}, [rememberPreScopedPanelZoom, scopedPanelSpotlightStopId, setUserZoom]);

	/**
	 * Display-frame shifts (preview sheet insets → rows/cols) remap absolute
	 * grid coords. Stale hold / previewMovementAnchor then sit off-path forever
	 * and findPathTileRoute returns null. Remap onto the new path frame and
	 * allow the current navigation token to retry.
	 */
	const previousDisplayFrameKeyForPreviewRef = useRef(displayFrameKey);
	useLayoutEffect(() => {
		if (previousDisplayFrameKeyForPreviewRef.current === displayFrameKey) {
			return;
		}
		previousDisplayFrameKeyForPreviewRef.current = displayFrameKey;
		if (scopedPanelSpotlightStopIdRef.current == null) {
			return;
		}
		pendingScopedPreviewWalkRef.current = null;
		setPreviewMovementAnchor(null);
		const remapTarget =
			playerAnchorCoordRef.current ??
			ensureOnPathCoord(
				characterMovementRef.current.resolvePathOriginCoord(),
				displayLayout.pathCoordSet,
				metrics,
				activePlayerCoordRef.current,
			);
		if (remapTarget != null) {
			characterMovementRef.current.snapToCoord(remapTarget);
		}
		if (
			pendingPreviewNavigationTokenRef.current != null ||
			lastCompletedPreviewNavigationTokenRef.current ===
				scopedPanelNavigationToken
		) {
			lastCompletedPreviewNavigationTokenRef.current = -1;
			pendingPreviewNavigationTokenRef.current = null;
		}
	}, [
		displayFrameKey,
		displayLayout.pathCoordSet,
		metrics,
		scopedPanelNavigationToken,
	]);

	useEffect(() => {
		const stopId = scopedPanelSpotlightStopId;
		if (stopId == null) {
			return;
		}
		const navigationToken = scopedPanelNavigationToken;
		if (navigationToken <= 0) {
			return;
		}
		if (
			lastCompletedPreviewNavigationTokenRef.current === navigationToken
		) {
			return;
		}
		const rawOrigin =
			characterMovementRef.current.resolvePathOriginCoord() ??
			activePlayerCoordRef.current ??
			playerAnchorCoordRef.current;
		const originCoord = ensureOnPathCoord(
			rawOrigin,
			displayLayout.pathCoordSet,
			metrics,
			playerAnchorCoordRef.current,
		);
		if (!originCoord) {
			return;
		}
		const targetCell = resolvePlayerPathCell(stopId, graph, displayLayout);
		if (targetCell == null) {
			return;
		}

		const previousStopId = previousSpotlightStopForFocusRef.current;
		const route = findPathTileRoute(
			originCoord,
			{ row: targetCell.row, col: targetCell.col },
			displayLayout.pathCoordSet,
		);
		const now =
			typeof performance !== "undefined" ? performance.now() : Date.now();
		let suppressMs = 280;
		if (route && route.length >= 2) {
			suppressMs = Math.max(
				280,
				(route.length - 1) * characterMovementMsPerTile + 120,
			);
		}
		scopedPanelLayoutRecenterSuppressUntilRef.current = now + suppressMs;

		pendingPreviewNavigationTokenRef.current = navigationToken;
		navigateCharacterToStopRef.current(stopId, { notifyPress: false });

		if (route == null || route.length < 2) {
			lastCompletedPreviewNavigationTokenRef.current = navigationToken;
			pendingPreviewNavigationTokenRef.current = null;
		}

		previousSpotlightStopForFocusRef.current = stopId;
		if (previousStopId != null && previousStopId !== stopId) {
			focusViewportOnSpotlightStopRef.current(stopId, { animate: true });
		}
	}, [
		activePlayerCoord,
		characterMovementMsPerTile,
		displayFrameKey,
		displayLayout,
		graph,
		metrics,
		scopedPanelNavigationToken,
		scopedPanelSpotlightStopId,
	]);

	const previousViewportFocusTokenRef = useRef<number | null>(null);
	useLayoutEffect(() => {
		const token = requestedViewportFocus?.token ?? null;
		const stopId = requestedViewportFocus?.stopId;
		if (token == null || stopId == null) {
			return;
		}
		if (previousViewportFocusTokenRef.current === token) {
			return;
		}
		previousViewportFocusTokenRef.current = token;

		let cancelled = false;
		let attempts = 0;
		const maxAttempts = 8;

		const runWhenViewportReady = () => {
			if (cancelled) {
				return;
			}
			attempts += 1;
			const viewport = panZoomViewportNodeRef.current;
			const viewportReady =
				viewport != null &&
				viewport.clientWidth > 0 &&
				viewport.clientHeight > 0;
			const containerReady =
				containerSizeRef.current.width > 0 &&
				containerSizeRef.current.height > 0;
			if ((!viewportReady || !containerReady) && attempts < maxAttempts) {
				requestAnimationFrame(runWhenViewportReady);
				return;
			}
			focusViewportOnSpotlightStopRef.current(stopId);
			syncScopedPanelSpotlightRouteForStopRef.current(stopId);
		};

		runWhenViewportReady();

		return () => {
			cancelled = true;
		};
	}, [requestedViewportFocus?.stopId, requestedViewportFocus?.token]);

	// Re-center after layout when the map stage / viewport / fit scale change (resize).
	// Stop changes while preview is open are handled above — do not add stopId to deps (flicker).
	useLayoutEffect(() => {
		const stopId = scopedPanelSpotlightStopIdRef.current;
		if (stopId == null) {
			return;
		}
		if (
			containerSize.width <= 0 ||
			containerSize.height <= 0 ||
			panZoomViewportSize.width <= 0 ||
			panZoomViewportSize.height <= 0
		) {
			return;
		}
		const insetsKey =
			scopedPanelSheetInsets != null
				? `${scopedPanelSheetInsets.topPx}:${scopedPanelSheetInsets.bottomPx}`
				: null;
		const hadMeasuredInsets =
			previousScopedPanelSheetInsetsKeyRef.current != null;
		const insetsChanged =
			insetsKey != null &&
			insetsKey !== previousScopedPanelSheetInsetsKeyRef.current;
		if (insetsKey != null) {
			previousScopedPanelSheetInsetsKeyRef.current = insetsKey;
		}
		// First measured insets: one-shot optical recenter after sheets land.
		// Do **not** refine on every subsequent insets tick — that bypassed the
		// suppress window and coupled with ScopedPanelHost RO → focus → layout
		// storm (structure-card → map preview freeze). CSS already applies
		// insets; later ±1px noise must not thrash the viewport.
		if (insetsChanged && !hadMeasuredInsets) {
			requestAnimationFrame(() => {
				if (scopedPanelSpotlightStopIdRef.current !== stopId) {
					return;
				}
				focusViewportOnSpotlightStopRef.current(stopId, {
					animate: false,
				});
			});
			return;
		}
		if (insetsChanged && hadMeasuredInsets) {
			return;
		}
		const now =
			typeof performance !== "undefined" ? performance.now() : Date.now();
		if (
			characterMovement.isMoving ||
			now < scopedPanelLayoutRecenterSuppressUntilRef.current
		) {
			return;
		}
		focusViewportOnSpotlightStopRef.current(stopId, { animate: false });
		syncScopedPanelSpotlightRouteForStopRef.current(stopId);
	}, [
		containerSize.height,
		containerSize.width,
		contentBounds.height,
		contentBounds.width,
		displayCols,
		displayRows,
		fitScale,
		logicalLayoutIdentity,
		metrics.hexagonHeight,
		metrics.hexagonWidth,
		scopedPanelSheetInsets?.bottomPx,
		scopedPanelSheetInsets?.topPx,
	]);

	const resolvePlayerZoomAnchor = useCallback(() => {
		const viewport = panZoomViewportNodeRef.current;
		if (!viewport || !restingDisplayCenter) {
			return null;
		}
		const rect = viewport.getBoundingClientRect();
		const contentCenterX = contentBounds.width / 2;
		const contentCenterY = contentBounds.height / 2;
		const offsetX =
			(restingDisplayCenter.left - contentCenterX) * scale + pan.x;
		const offsetY =
			(restingDisplayCenter.top - contentCenterY) * scale + pan.y;
		return {
			anchorClientX: rect.left + rect.width / 2 + offsetX,
			anchorClientY: rect.top + rect.height / 2 + offsetY,
		};
	}, [
		contentBounds.height,
		contentBounds.width,
		pan.x,
		pan.y,
		restingDisplayCenter,
		scale,
	]);

	const setZoomAnchoredToPlayer = useCallback(
		(targetZoom: number, options?: { rapid?: boolean }) => {
			const anchor = resolvePlayerZoomAnchor();
			setUserZoom(
				targetZoom,
				anchor
					? {
							...anchor,
							rapid: options?.rapid ?? false,
						}
					: { rapid: options?.rapid ?? false },
			);
		},
		[resolvePlayerZoomAnchor, setUserZoom],
	);

	const handleZoomChangeFromControls = useCallback(
		(targetZoom: number) => {
			// Same path as +/-: animated transform, player-anchored zoom.
			setZoomAnchoredToPlayer(targetZoom);
		},
		[setZoomAnchoredToPlayer],
	);

	const handleZoomInFromControls = useCallback(() => {
		setZoomAnchoredToPlayer(userZoom + userZoomStep);
	}, [setZoomAnchoredToPlayer, userZoom, userZoomStep]);

	const handleZoomOutFromControls = useCallback(() => {
		setZoomAnchoredToPlayer(userZoom - userZoomStep);
	}, [setZoomAnchoredToPlayer, userZoom, userZoomStep]);
	const hexagonInnerScale = resolveHexagonInnerScale(
		metrics.hexagonWidth,
		metrics.hexagonMargin,
	);
	const resolvedPathTileBorderWidthPx =
		pathTileBorderWidthPx ??
		Math.max(
			HEXAGON_PATH_TILE_DEFAULT_BORDER_WIDTH_PX,
			Math.round(metrics.hexagonMargin),
		);
	const pathTileClipPaths = useMemo(
		() =>
			buildHexagonPathTileClipPaths({
				outerCornerRadiusPx: pathTileOuterCornerRadiusPx,
				borderWidthPx: resolvedPathTileBorderWidthPx,
				hexagonWidth: metrics.hexagonWidth,
				hexagonHeight: metrics.hexagonHeight,
			}),
		[
			metrics.hexagonHeight,
			metrics.hexagonWidth,
			pathTileOuterCornerRadiusPx,
			resolvedPathTileBorderWidthPx,
		],
	);
	const pathTileFaceScale = useMemo(
		() =>
			resolveHexagonPathFaceScale({
				innerScale: hexagonInnerScale,
				hexagonWidth: metrics.hexagonWidth,
				hexagonHeight: metrics.hexagonHeight,
			}),
		[hexagonInnerScale, metrics.hexagonHeight, metrics.hexagonWidth],
	);

	const hasLayoutError = !layoutResult.layout;

	const showMapActionControls =
		!hasLayoutError && containerSize.width > 0 && containerSize.height > 0;

	const thumbMovementDisabled =
		!showThumbMovementControls ||
		!showMapActionControls ||
		isScopedPanelOpen ||
		!characterIsOnMap;

	const {
		handlePointerDriveEnd,
		handlePointerInteractionChange,
		handleThumbDriveChange,
		thumbSyncedVisual,
	} = useHexagonMapThumbMovement({
		characterMovement,
		characterMovementMsPerTile,
		commitPlayerPathPositionForCoord,
		disabled: thumbMovementDisabled,
		furthestUnlockedPathProgress,
		isPathIndexVisible: isPathIndexVisibleForMovement,
		keyboardEnabled:
			showThumbMovementControls &&
			showMapActionControls &&
			!isScopedPanelOpen,
		metrics,
		onMapPathSelectionChange,
		pathCellByCoord,
		pathCoordSet: displayLayout.pathCoordSet,
		pathIndexByCoordKey,
		pathProgressByCoordKey,
		restingDisplayCenter,
	});

	/**
	 * Coarse boolean only — do not subscribe the whole HexagonMap to every
	 * mover rAF snapshot (that re-renders the tile tree and teleports the doll).
	 */
	const [isPlayerCenteredInViewport, setIsPlayerCenteredInViewport] =
		useState(false);
	useEffect(() => {
		const resolveCentered = (): boolean => {
			if (
				panZoomViewportSize.width <= 0 ||
				panZoomViewportSize.height <= 0 ||
				contentBounds.width <= 0 ||
				contentBounds.height <= 0
			) {
				return false;
			}
			const characterCenter = resolveCharacterRenderCenter(
				characterMovement.getDisplaySnapshot(),
				restingDisplayCenter,
			);
			if (!characterCenter) {
				return false;
			}
			return isContentPointCenteredInViewport({
				contentPoint: {
					x: characterCenter.left,
					y: characterCenter.top,
				},
				contentWidth: contentBounds.width,
				contentHeight: contentBounds.height,
				viewportWidth: panZoomViewportSize.width,
				viewportHeight: panZoomViewportSize.height,
				pan,
				scale,
			});
		};
		const sync = () => {
			const next = resolveCentered();
			setIsPlayerCenteredInViewport((prev) =>
				prev === next ? prev : next,
			);
		};
		sync();
		return characterMovement.subscribeDisplay(sync);
	}, [
		characterMovement,
		contentBounds.height,
		contentBounds.width,
		pan,
		panZoomViewportSize.height,
		panZoomViewportSize.width,
		restingDisplayCenter,
		scale,
	]);

	const canCenterOnPlayerFromControls =
		showMapActionControls &&
		characterIsOnMap &&
		userZoom > HEXAGON_MAP_MIN_USER_ZOOM &&
		!isPlayerCenteredInViewport;

	const canReturnToMapViewFromControls =
		showMapActionControls &&
		characterIsOnMap &&
		userZoom > HEXAGON_MAP_MIN_USER_ZOOM &&
		isPlayerCenteredInViewport;

	const handleCenterOnPlayerFromControls = useCallback(() => {
		const center = resolveCharacterRenderCenter(
			characterMovement.getDisplaySnapshot(),
			restingDisplayCenter,
		);
		if (center) {
			panToCenterContentPoint({
				x: center.left,
				y: center.top,
			});
		}
	}, [characterMovement, panToCenterContentPoint, restingDisplayCenter]);

	const handleReturnToMapViewFromControls = useCallback(() => {
		setZoomAnchoredToPlayer(HEXAGON_MAP_MIN_USER_ZOOM, { rapid: false });
	}, [setZoomAnchoredToPlayer]);

	const canFastForwardToFurthestStop =
		showMapActionControls &&
		characterIsOnMap &&
		furthestUnlockedStopOnPath != null &&
		!isPlayerOnFurthestUnlockedStop;

	const handleFastForwardToFurthestStop = useCallback(() => {
		const target = furthestUnlockedStopOnPath;
		if (!target) {
			return;
		}
		handlePathTilePress({ row: target.row, col: target.col });
	}, [furthestUnlockedStopOnPath, handlePathTilePress]);

	const mapContentStageStyle = useMemo(
		() => ({
			...panZoomContentStyle,
			["--hex-map-hover-scale" as string]: HEXAGON_MAP_HOVER_SCALE,
			["--hexagon-width" as string]: `${metrics.hexagonWidth}px`,
			["--hexagon-height" as string]: `${metrics.hexagonHeight}px`,
			["--hexagon-margin-px" as string]: `${metrics.hexagonMargin}px`,
			["--hexagon-inner-scale" as string]: `${hexagonInnerScale}`,
			["--hexagon-path-face-scale" as string]: `${pathTileFaceScale}`,
			["--stop-overlay-size" as string]: `${stopOverlaySize}px`,
			["--hex-tile-outer-clip" as string]:
				pathTileClipPaths.outerClipPath,
			["--hex-tile-inner-clip" as string]:
				pathTileClipPaths.innerClipPath,
		}),
		[
			hexagonInnerScale,
			metrics.hexagonHeight,
			metrics.hexagonMargin,
			metrics.hexagonWidth,
			pathTileClipPaths.innerClipPath,
			pathTileClipPaths.outerClipPath,
			pathTileFaceScale,
			panZoomContentStyle,
			stopOverlaySize,
		],
	);

	return (
		<div
			ref={setContainerRef}
			// biome-ignore lint/a11y/noNoninteractiveTabindex: the map container must be focusable so keyboard path navigation can start from the stage.
			tabIndex={0}
			role="application"
			aria-label="Module path map"
			className={cn(
				"relative flex min-h-0 flex-1 flex-col overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2",
				className,
			)}
		>
			<style>{`
				@keyframes sq-hex-map-scale-fade-in {
					0% {
						opacity: 0;
						transform: scale(1);
					}
					35% {
						opacity: 0.25;
						transform: scale(1.12);
					}
					70% {
						opacity: 0.75;
						transform: scale(var(--hex-map-hover-scale, 1.5));
					}
					100% {
						opacity: 1;
						transform: scale(1);
					}
				}
				@keyframes sq-hex-map-scale-fade-out {
					0% {
						opacity: 1;
						transform: scale(1);
					}
					30% {
						opacity: 0.85;
						transform: scale(1.12);
					}
					70% {
						opacity: 0.35;
						transform: scale(var(--hex-map-hover-scale, 1.5));
					}
					100% {
						opacity: 0;
						transform: scale(1);
					}
				}
				.sq-hex-map-character-layout-exit {
					animation-name: sq-hex-map-scale-fade-out;
					animation-timing-function: cubic-bezier(.4, 2, .35, 1);
					animation-fill-mode: both;
				}
				.sq-hex-map-character-layout-enter {
					animation-name: sq-hex-map-scale-fade-in;
					animation-timing-function: cubic-bezier(.4, 2, .35, 1);
					animation-fill-mode: both;
				}
				@keyframes sq-hex-map-wipe-in {
					from {
						opacity: 0;
						clip-path: inset(100% 0 0 0);
					}
					to {
						opacity: 1;
						clip-path: inset(0 0 0 0);
					}
				}
				@keyframes sq-hex-map-wipe-out {
					from {
						opacity: 1;
						clip-path: inset(0 0 0 0);
					}
					to {
						opacity: 0;
						clip-path: inset(0 0 100% 0);
					}
				}
			`}</style>
			<div
				ref={handlePanZoomViewportRef}
				className="relative z-10 min-h-0 flex-1 overflow-hidden overscroll-contain touch-none outline-none [--map-scale:1]"
				role="application"
				aria-label="Interactive hexagon map"
				onPointerDown={handleViewportPointerDown}
				onPointerUp={handleViewportPointerUp}
				onPointerLeave={handleViewportPointerLeave}
			>
				{hasLayoutError ? (
					<div className="flex min-h-full min-w-full items-center justify-center p-4 pt-20">
						<div className="max-w-sm rounded-xl border border-white/10 bg-black/50 p-5 text-center text-white/90 shadow-2xl backdrop-blur-md">
							<p className="font-semibold text-sm">
								Map layout unavailable
							</p>
							<p className="mt-2 text-sm text-white/75">
								We&apos;re sorry, that combination of settings
								doesn&apos;t generate a valid map. Please adjust
								your settings or try again by regenerating with
								a new seed.
							</p>
							{layoutErrorActions ? (
								<div className="mt-4 flex w-full flex-row gap-2">
									{layoutErrorActions}
								</div>
							) : (
								<p className="mt-3 text-xs text-white/45">
									{getLayoutErrorMessage(layoutResult.error)}
								</p>
							)}
						</div>
					</div>
				) : (
					<>
						<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
							<div
								ref={setContentStageRef}
								className={cn(
									"relative shrink-0",
									isScopedPanelOpen
										? "pointer-events-none"
										: "pointer-events-auto",
									isHoveredTraversablePathTile &&
										"cursor-pointer",
									isHoveredLockedSpacerPathTile &&
										"cursor-not-allowed",
								)}
								style={mapContentStageStyle}
								onPointerDown={handleContentPointerDown}
								onPointerMove={handleContentPointerMove}
								onPointerUp={handleContentPointerUp}
								onPointerLeave={handleContentPointerLeave}
							>
								<HexagonMapBackdrop backdropJpg={backdropJpg} />
								<HexagonMapLayerProfiler id="hexagon-map-grid-layer">
									<HexagonGridLayer
										cells={visualGridCells}
										isTranslucent={isTranslucent}
										animationMode={gridAnimationMode}
										animationDurationMs={
											gridAnimationDurationMs
										}
										positionTransitionMs={
											contentPositionTransitionMs
										}
										registerHoverCellRoot={
											registerHoverCellRoot
										}
									/>
								</HexagonMapLayerProfiler>
								<HexagonMapLayerProfiler id="hexagon-map-path-connector-layer">
									<HexagonPathConnectorLayer
										cells={pathOverlayCells}
										metrics={metrics}
										contentWidth={contentBounds.width}
										contentHeight={contentBounds.height}
										borderWidthPx={
											resolvedPathTileBorderWidthPx
										}
										activePlayerCoord={
											characterPathVisualActiveCoord
										}
										isPathIndexVisible={isPathIndexVisible}
										lockedPathCoordKeys={
											lockedPathCoordKeys
										}
										scopedPanelSpotlightRenderCoordKeys={
											scopedPanelSpotlightRenderCoordKeys
										}
										layerPass={
											isScopedPanelOpen
												? "below-dim"
												: "full"
										}
										reducedMotion={Boolean(reducedMotion)}
									/>
								</HexagonMapLayerProfiler>
								<HexagonMapLayerProfiler id="hexagon-map-path-layer">
									<HexagonPathOverlayLayer
										cells={pathOverlayCells}
										isTranslucent={isTranslucent}
										activePlayerCoord={
											characterPathVisualActiveCoord
										}
										isPathIndexVisible={isPathIndexVisible}
										positionTransitionMs={
											contentPositionTransitionMs
										}
										registerHoverCellRoot={
											registerHoverCellRoot
										}
										scopedPanelSpotlightRenderCoordKeys={
											scopedPanelSpotlightRenderCoordKeys
										}
										scopedPanelSpotlightActiveRouteCoordKeys={
											scopedPanelSpotlightActiveRouteCoordKeys
										}
										scopedPanelSpotlightExitingTiles={
											scopedPanelSpotlightExitingTiles
										}
										lockedPathCoordKeys={
											lockedPathCoordKeys
										}
										layerPass={
											isScopedPanelOpen
												? "below-dim"
												: "full"
										}
									/>
								</HexagonMapLayerProfiler>
								<HexagonMapLayerProfiler id="hexagon-map-environment-layer">
									{renderEnvironmentPlacements.map(
										(placement) => (
											<HexagonEnvironmentSprite
												key={`env:${placement.key}`}
												model={placement}
												animationMode={
													gridAnimationMode
												}
												animationDurationMs={
													gridAnimationDurationMs
												}
												positionTransitionMs={
													contentPositionTransitionMs
												}
											/>
										),
									)}
								</HexagonMapLayerProfiler>
								{!reducedMotion ? (
									<div className="pointer-events-none absolute inset-0 z-[15]">
										<Particles
											className="size-full"
											quantity={200}
										/>
									</div>
								) : null}
								<HexagonMapLayerProfiler id="hexagon-map-stop-layer">
									<HexagonStopLayer
										stopCells={stopCells}
										hoveredHex={hoveredHex}
										focusedPathIndex={focusedPathIndex}
										demoMode={demoMode}
										pathStopIcons={pathStopIcons}
										renderPathStopTooltip={
											renderPathStopTooltip
										}
										highlightedStopId={highlightedStopId}
										stopButtonRefs={stopButtonRefs}
										handlePathStopClick={
											handlePathStopClick
										}
										isPathIndexVisible={isPathIndexVisible}
										activePlayerCoord={activePlayerCoord}
										positionTransitionMs={
											contentPositionTransitionMs
										}
										scopedPanelSpotlightRenderCoordKeys={
											scopedPanelSpotlightRenderCoordKeys
										}
										suppressStopTooltips={isScopedPanelOpen}
										layerPass={
											isScopedPanelOpen
												? "below-dim"
												: "full"
										}
									/>
								</HexagonMapLayerProfiler>
								{isScopedPanelOpen ? (
									<HexagonMapLayerProfiler id="hexagon-map-stop-speech-bubble-layer-below-dim">
										<HexagonStopSpeechBubbleLayer
											stopCells={stopCells}
											contentMapScale={scale}
											userZoom={userZoom}
											hexagonHeight={
												metrics.hexagonHeight
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											positionTransitionMs={
												contentPositionTransitionMs
											}
											scopedPanelSpotlightStopId={
												scopedPanelSpotlightStopId
											}
											scopedPanelPreviewSpeechBubble={
												scopedPanelPreviewSpeechBubble
											}
											layerPass="below-dim"
										/>
									</HexagonMapLayerProfiler>
								) : (
									<HexagonMapLayerProfiler id="hexagon-map-stop-speech-bubble-layer">
										<HexagonStopSpeechBubbleLayer
											stopCells={stopCells}
											contentMapScale={scale}
											userZoom={userZoom}
											hexagonHeight={
												metrics.hexagonHeight
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											positionTransitionMs={
												contentPositionTransitionMs
											}
											scopedPanelSpotlightStopId={
												scopedPanelSpotlightStopId
											}
											scopedPanelPreviewSpeechBubble={
												scopedPanelPreviewSpeechBubble
											}
											layerPass="full"
										/>
									</HexagonMapLayerProfiler>
								)}
								{pathStopBadges ? (
									<HexagonStopBadgeLayer
										stopCells={stopCells}
										badges={pathStopBadges}
										contentMapScale={scale}
										hexagonHeight={metrics.hexagonHeight}
										isPathIndexVisible={isPathIndexVisible}
										positionTransitionMs={
											contentPositionTransitionMs
										}
									/>
								) : null}
							</div>
						</div>
						{/* Dim + spotlights are viewport siblings (not inside the pan/zoom transform). backdrop-filter on a transformed ancestor breaks z-order after resize. */}
						{mapDimOverlay.mounted ? (
							<div
								className={cn(
									"pointer-events-none absolute inset-0",
									scopedPanelOverlayClassName,
									scopedPanelMapOverlayFadeClassName,
									mapDimOverlay.visible
										? "opacity-100"
										: "opacity-0",
								)}
								style={{ zIndex: SCOPED_PANEL_MAP_DIM_Z_INDEX }}
								aria-hidden
							/>
						) : null}
						{isScopedPanelOpen ? (
							<div
								className="pointer-events-none absolute inset-0 flex items-center justify-center"
								style={{
									zIndex: SCOPED_PANEL_MAP_SPOTLIGHT_Z_INDEX,
								}}
							>
								<div
									className="relative shrink-0"
									style={mapContentStageStyle}
								>
									<HexagonMapLayerProfiler id="hexagon-map-path-connector-layer-spotlight">
										<HexagonPathConnectorLayer
											cells={pathOverlayCells}
											metrics={metrics}
											contentWidth={contentBounds.width}
											contentHeight={contentBounds.height}
											borderWidthPx={
												resolvedPathTileBorderWidthPx
											}
											activePlayerCoord={
												characterPathVisualActiveCoord
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											lockedPathCoordKeys={
												lockedPathCoordKeys
											}
											scopedPanelSpotlightRenderCoordKeys={
												scopedPanelSpotlightRenderCoordKeys
											}
											layerPass="above-dim"
											reducedMotion={Boolean(
												reducedMotion,
											)}
										/>
									</HexagonMapLayerProfiler>
									<HexagonMapLayerProfiler id="hexagon-map-path-layer-spotlight">
										<HexagonPathOverlayLayer
											cells={pathOverlayCells}
											isTranslucent={isTranslucent}
											activePlayerCoord={
												characterPathVisualActiveCoord
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											positionTransitionMs={
												contentPositionTransitionMs
											}
											registerHoverCellRoot={
												registerHoverCellRoot
											}
											scopedPanelSpotlightRenderCoordKeys={
												scopedPanelSpotlightRenderCoordKeys
											}
											scopedPanelSpotlightActiveRouteCoordKeys={
												scopedPanelSpotlightActiveRouteCoordKeys
											}
											scopedPanelSpotlightExitingTiles={
												scopedPanelSpotlightExitingTiles
											}
											lockedPathCoordKeys={
												lockedPathCoordKeys
											}
											layerPass="above-dim"
										/>
									</HexagonMapLayerProfiler>
									<HexagonMapLayerProfiler id="hexagon-map-stop-layer-spotlight">
										<HexagonStopLayer
											stopCells={stopCells}
											hoveredHex={hoveredHex}
											focusedPathIndex={focusedPathIndex}
											demoMode={demoMode}
											pathStopIcons={pathStopIcons}
											renderPathStopTooltip={
												renderPathStopTooltip
											}
											highlightedStopId={
												highlightedStopId
											}
											stopButtonRefs={stopButtonRefs}
											handlePathStopClick={
												handlePathStopClick
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											activePlayerCoord={
												activePlayerCoord
											}
											positionTransitionMs={
												contentPositionTransitionMs
											}
											scopedPanelSpotlightRenderCoordKeys={
												scopedPanelSpotlightRenderCoordKeys
											}
											suppressStopTooltips
											layerPass="above-dim"
										/>
									</HexagonMapLayerProfiler>
									<HexagonMapLayerProfiler id="hexagon-map-stop-speech-bubble-layer-spotlight">
										<HexagonStopSpeechBubbleLayer
											stopCells={stopCells}
											contentMapScale={scale}
											userZoom={userZoom}
											hexagonHeight={
												metrics.hexagonHeight
											}
											isPathIndexVisible={
												isPathIndexVisible
											}
											positionTransitionMs={
												contentPositionTransitionMs
											}
											scopedPanelSpotlightStopId={
												scopedPanelSpotlightStopId
											}
											scopedPanelPreviewSpeechBubble={
												scopedPanelPreviewSpeechBubble
											}
											layerPass="above-dim"
										/>
									</HexagonMapLayerProfiler>
								</div>
							</div>
						) : null}
						{/**
						 * Single persistent doll host — viewport sibling with the same
						 * pan/zoom stage as map content. Must not remount when stop
						 * preview opens (that tore down WebGL and the player vanished).
						 */}
						{characterIsOnMap ? (
							<div
								className="pointer-events-none absolute inset-0 flex items-center justify-center"
								style={{
									zIndex: isScopedPanelOpen
										? SCOPED_PANEL_MAP_SPOTLIGHT_CHARACTER_Z_INDEX
										: 40,
								}}
							>
								<div
									className="relative shrink-0"
									style={mapContentStageStyle}
								>
									<HexagonMapPlayerCharacterHost
										subscribeDisplay={
											characterMovement.subscribeDisplay
										}
										getDisplaySnapshot={
											characterMovement.getDisplaySnapshot
										}
										restingCenter={
											characterRestingCenterForSprite
										}
										isVisible={characterIsOnMap}
										stopOverlaySize={characterOverlaySize}
										contentPositionTransitionMs={
											contentPositionTransitionMs
										}
										contentMapScale={scale}
										userZoom={userZoom}
										playerPinSrc={playerPinSrc}
										isScopedPanelSpotlight={
											isScopedPanelOpen
										}
										hidePlayerPin={isScopedPanelOpen}
										layoutMotionPhase={
											characterLayoutMotionPhase
										}
										layoutMotionDurationMs={
											gridAnimationDurationMs
										}
										renderPlayerCharacter={
											renderPlayerCharacter
										}
									/>
								</div>
							</div>
						) : null}
						<SurfaceEdgeVignette
							zIndex={SCOPED_PANEL_MAP_VIGNETTE_Z_INDEX}
						/>
						{previewerChrome ? (
							<div
								className="pointer-events-none absolute inset-0"
								style={{
									zIndex: isScopedPanelOpen
										? SCOPED_PANEL_MAP_PREVIEWER_CHROME_Z_INDEX
										: 30,
								}}
							>
								{previewerChrome}
							</div>
						) : null}
						{mapDimOverlay.mounted && onScopedPanelDismiss ? (
							<button
								type="button"
								className={cn(
									"absolute inset-0 touch-none bg-transparent",
									mapDimOverlay.visible
										? "pointer-events-auto"
										: "pointer-events-none",
								)}
								style={{ zIndex: SCOPED_PANEL_MAP_DIM_Z_INDEX }}
								aria-label="Dismiss preview"
								onClick={onScopedPanelDismiss}
							/>
						) : null}
					</>
				)}
				{playerAvatarSrc && characterIsOnMap && !hasLayoutError ? (
					<HexagonMapPlayerFinder
						playerAvatarSrc={playerAvatarSrc}
						viewportWidth={panZoomViewportSize.width}
						viewportHeight={panZoomViewportSize.height}
						contentWidth={contentBounds.width}
						contentHeight={contentBounds.height}
						pan={pan}
						scale={scale}
						characterViewportMargin={characterOverlaySize * scale}
						subscribeDisplay={characterMovement.subscribeDisplay}
						getDisplaySnapshot={
							characterMovement.getDisplaySnapshot
						}
						restingCenter={restingDisplayCenter}
						isCharacterVisible={characterIsOnMap}
						onPanToCharacter={handlePanToCharacter}
						isScopedPanelOpen={isScopedPanelOpen}
					/>
				) : null}
			</div>
			<HexagonMapControls
				userZoom={userZoom}
				onZoomIn={handleZoomInFromControls}
				onZoomOut={handleZoomOutFromControls}
				onZoomChange={handleZoomChangeFromControls}
				isScopedPanelOpen={isScopedPanelOpen}
				onCenterOnPlayer={
					showMapActionControls
						? handleCenterOnPlayerFromControls
						: undefined
				}
				canCenterOnPlayer={canCenterOnPlayerFromControls}
				onReturnToMapView={
					showMapActionControls
						? handleReturnToMapViewFromControls
						: undefined
				}
				canReturnToMapView={canReturnToMapViewFromControls}
				onFastForwardToFurthestStop={
					showMapActionControls
						? handleFastForwardToFurthestStop
						: undefined
				}
				canFastForwardToFurthestStop={canFastForwardToFurthestStop}
				topLeftOctagonButton={topLeftOctagonButton}
				topRightOctagonButton={topRightOctagonButton}
				thumbMovementControls={
					showThumbMovementControls && showMapActionControls
						? {
								disabled: thumbMovementDisabled,
								onDriveChange: handleThumbDriveChange,
								onDriveEnd: handlePointerDriveEnd,
								onPointerInteractionChange:
									handlePointerInteractionChange,
								syncedVisual: thumbSyncedVisual,
							}
						: undefined
				}
			/>
		</div>
	);
}
